import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, HStack, VStack, Text, Pressable, useMediaQuery, Avatar } from "native-base";
import { MOBILE_VIEW_WIDTH } from "../../utils/screens";
import MobileHeader from "../../layout/header/mobileHeader";
import WebHeader from "../../layout/header/webHeader";
import CustomButton from "../../components/button";
import Dropdown from "../../components/dropdown";
import SearchableDropdown from "../../components/searchableDropDown";
import InputField from "../../components/input";
import ViewUploadImageCards from "../../containers/viewUploadImageCards";
import { COLORS } from "../../utils/colors";
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from "../../utils/fonts";
import { deleteCardsIconSVG, addIconSVG, editIconBannerImage, noPhotosUploadedSVG } from "../../utils/svg";
import { Svg } from "react-native-svg";
import CommonHeading from "../../components/headings";
import { useHistory } from "react-router-dom";
import Breadcrumbs from "../../components/breadcrumb";
import CryptoJS from "crypto-js";
import config from "../../utils/config";
import { activeEventTab } from "../../store/reducers/eventSlice";
import { getDropdownDataAPI, uploadQuotationAPI, editQuotationAPI, listQuotationsAPI, requestEditQuotationAPI } from "../../api/apis/quicklinks";
import { Toaster } from "../../components/toaster";
import { LoaderAnimation } from "../../components/spinner";
import CustomModal from "../../components/modal";
import MobileModelSheet from "../../components/actionSheet";
import { statusMap } from "../../utils/permission";

const QuotationsPage = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const [MobileWindow] = useMediaQuery({ maxWidth: MOBILE_VIEW_WIDTH });
    const eventsDetail = useSelector((state) => state.event.selectedEvent);
    const activeTab = useSelector((state) => state.event.activeTab);

    // Get event data from session storage (for immediate use on refresh)
    const getEventFromStorage = () => {
        try {
            const eventBytes = CryptoJS.AES.decrypt(
                sessionStorage.getItem("event"),
                config.eventKey
            );
            const eventString = eventBytes.toString(CryptoJS.enc.Utf8);
            return JSON.parse(eventString);
        } catch (e) {
            return null;
        }
    };

    // Keep a copy of the current event in session storage so refresh has status/agency
    useEffect(() => {
        if (!eventsDetail?.id) return;
        try {
            const storedEvent = getEventFromStorage();
            if (!storedEvent || storedEvent?.id !== eventsDetail.id) {
                sessionStorage.setItem(
                    "event",
                    CryptoJS.AES.encrypt(
                        JSON.stringify(eventsDetail),
                        config.eventKey
                    ).toString()
                );
            }
        } catch (e) {
            // no-op: session storage not available
        }
    }, [eventsDetail?.id]);

    // Restore active tab from session storage on mount if Redux state is empty or wrong
    useEffect(() => {
        const storedEvent = getEventFromStorage();
        if (storedEvent?.type && (storedEvent.type === "event" || storedEvent.type === "mice")) {
            // Only update if Redux state is empty or doesn't match
            if (!activeTab || (activeTab !== "event" && activeTab !== "mice") || activeTab !== storedEvent.type) {
                dispatch(activeEventTab(storedEvent.type));
            }
        }
    }, [dispatch]);

    // Get event name from Redux state or try to get from session storage as fallback
    const getEventName = () => {
        if (eventsDetail?.event_name) {
            return eventsDetail.event_name;
        }
        // Try to get from session storage as fallback
        const storedEvent = getEventFromStorage();
        if (storedEvent?.event_name) {
            return storedEvent.event_name;
        }
        return "Event Details";
    };

    // Get active tab from session storage first (for breadcrumb persistence on refresh)
    // This ensures correct text even if Redux hasn't updated yet
    const getActiveTabForBreadcrumb = () => {
        // Always check session storage first for breadcrumb (to handle refresh)
        const storedEvent = getEventFromStorage();
        if (storedEvent?.type && (storedEvent.type === "event" || storedEvent.type === "mice")) {
            return storedEvent.type;
        }
        // Fallback to Redux if session storage doesn't have it
        if (activeTab === "event" || activeTab === "mice") {
            return activeTab;
        }
        return "mice"; // Default
    };

    const eventName = getEventName();
    const currentActiveTab = getActiveTabForBreadcrumb();
    const eventTab = currentActiveTab === "event" ? "Event" : "MICE";

    // Breadcrumb configuration
    const breadcrumbDetails = {
        activeTab: { name: "Home", path: "/home" },
        activeMenu: { name: `${eventTab} Detail`, path: "/event-detail" },
        activeActivePage: { name: eventName, path: "/event-detail/quotations" },
    };

    const toaster = Toaster();

    // Get user info
    const getUserInfo = () => {
        try {
            const bytes = CryptoJS.AES.decrypt(
                sessionStorage.getItem("content"),
                config.userKey
            );
            const jsonString = bytes.toString(CryptoJS.enc.Utf8);
            return JSON.parse(jsonString);
        } catch (e) {
            return null;
        }
    };

    const userInfo = getUserInfo();

    // Get travel_partner_id from eventsDetail.agency based on user email
    // Uses Redux state first, falls back to session storage on refresh
    const getTravelPartnerId = () => {
        const userInfo = getUserInfo();

        // Get agency data from Redux or session storage
        let agencyData = eventsDetail?.agency;
        if (!agencyData || agencyData.length === 0) {
            const storedEvent = getEventFromStorage();
            agencyData = storedEvent?.agency || [];
        }

        if (!agencyData || agencyData.length === 0) {
            return null;
        }

        // If user is a vendor, try to find their agency by email match
        if (userInfo?.email) {
            const userAgency = agencyData.find((agency) => {
                if (Array.isArray(agency.emailId)) {
                    return agency.emailId.includes(userInfo.email);
                }
                return agency.emailId === userInfo.email;
            });
            if (userAgency?.id) {
                return userAgency.id;
            }
        }

        // If no match found and user is vendor, use first active agency
        // Otherwise, use first agency as fallback
        const activeAgency = agencyData.find(agency => agency.status === 'active');
        if (activeAgency?.id) {
            return activeAgency.id;
        }

        // Final fallback: use first agency's id
        return agencyData[0]?.id || null;
    };

    const getVendorAgency = () => {
        const userInfo = getUserInfo();
        let agencyData = eventsDetail?.agency;
        if (!agencyData || agencyData.length === 0) {
            const storedEvent = getEventFromStorage();
            agencyData = storedEvent?.agency || [];
        }

        if (!agencyData || agencyData.length === 0) {
            return null;
        }

        if (userInfo?.email) {
            const userAgency = agencyData.find((agency) => {
                if (Array.isArray(agency.emailId)) {
                    return agency.emailId.includes(userInfo.email);
                }
                return agency.emailId === userInfo.email;
            });
            if (userAgency) {
                return userAgency;
            }
        }

        return agencyData.find((agency) => agency.status === "active") || agencyData[0] || null;
    };

    const getEventStatus = () => {
        if (eventsDetail?.status) return eventsDetail.status;
        const storedEvent = getEventFromStorage();
        return storedEvent?.status || "";
    };

    const eventStatus = getEventStatus();
    const isEventCompleted = statusMap.indexOf(eventStatus) >= 3;
    const isAddInvoiceFlow =
        userInfo?.role === "vendor" &&
        isEventCompleted &&
        getVendorAgency()?.status === "active";
    const isQuotationReadOnly =
        userInfo?.role === "vendor" &&
        isEventCompleted &&
        getVendorAgency()?.status === "inactive";
    const groupBorderColor = "#C7CCD3";
        console.log(isAddInvoiceFlow,'check')
        console.log(userInfo.role)
        console.log(isEventCompleted)
        console.log(getVendorAgency()?.status)


    // Function to pre-fill quotation data into rows
    const prefillQuotationData = async (quotation) => {
        if (!quotation?.quotations || quotation.quotations.length === 0) {
            return;
        }

        // Flatten the nested structure: quotations -> sub_heads -> items
        const flatItems = [];
        quotation.quotations.forEach((quotationItem) => {
            const parentHeadValue = quotationItem.head || "";
            const parentHeadId = quotationItem.head_id || "";

            // Handle all items including miscellaneous - they all have sub_heads structure
            if (quotationItem.sub_heads && quotationItem.sub_heads.length > 0) {
                quotationItem.sub_heads.forEach((subHead) => {
                    const subHeadValue = subHead.sub_head || "";
                    const subHeadId = subHead.sub_head_id || "";

                    if (subHead.items && subHead.items.length > 0) {
                        subHead.items.forEach((item) => {
                            // Each item has its own head_id - use that instead of parent's head_id
                            const itemHeadId = item.head_id || parentHeadId || "";
                            const isMiscellaneous = itemHeadId && itemHeadId.toLowerCase() === "miscellaneous";
                            
                            // For miscellaneous items, preserve sub_head and details from API response
                            // For non-miscellaneous items, use details
                            flatItems.push({
                                head: isMiscellaneous ? "Miscellaneous" : (parentHeadValue || ""),
                                head_id: isMiscellaneous ? "miscellaneous" : itemHeadId,
                                sub_head: isMiscellaneous ? (item.sub_head || subHeadValue || "") : subHeadValue,
                                sub_head_id: isMiscellaneous ? "" : (item.sub_head_id || subHeadId || ""),
                                details: isMiscellaneous ? (item.details || "") : (item.details || ""),
                                description: isMiscellaneous ? (item.description || "") : "",
                                item_id: isMiscellaneous ? null : (item.item_id || ""),
                                size: item.size || "",
                                qty: item.qty || "",
                                benchmark_price: item.benchmark_price || 0,
                                invoice_size: item.invoice_size ?? "",
                                invoice_quantity: item.invoice_quantity ?? "",
                                invoice_price: item.invoice_price ?? "",
                                remarks: item.remarks || "",
                            });
                        });
                    }
                });
            } else {
                // Handle items that might be stored directly without sub_heads structure
                // Check if the quotationItem itself is an item
                const itemHeadId = quotationItem.head_id || "";
                const isMiscellaneous = itemHeadId && itemHeadId.toLowerCase() === "miscellaneous";
                
                if (isMiscellaneous || quotationItem.description || quotationItem.details) {
                    flatItems.push({
                        head: isMiscellaneous ? "Miscellaneous" : (parentHeadValue || ""),
                        head_id: isMiscellaneous ? "miscellaneous" : itemHeadId,
                        sub_head: isMiscellaneous ? (quotationItem.sub_head || "") : "",
                        sub_head_id: "",
                        details: isMiscellaneous ? (quotationItem.details || "") : (quotationItem.details || ""),
                        description: isMiscellaneous ? (quotationItem.description || "") : "",
                        item_id: isMiscellaneous ? null : (quotationItem.item_id || ""),
                        size: quotationItem.size || "",
                        qty: quotationItem.qty || "",
                        benchmark_price: quotationItem.benchmark_price || 0,
                        invoice_size: quotationItem.invoice_size ?? "",
                        invoice_quantity: quotationItem.invoice_quantity ?? "",
                        invoice_price: quotationItem.invoice_price ?? "",
                        remarks: quotationItem.remarks || "",
                    });
                }
            }
        });

        if (flatItems.length === 0) {
            return;
        }

        // Fetch sub_heads and details options for all unique head_id and sub_head_id combinations
        // Use Promise.all to fetch in parallel and avoid blocking
        const uniqueHeadIds = [...new Set(flatItems.map(item => item.head_id).filter(Boolean))];

        // Store head_id and sub_head_id pairs as objects to avoid splitting issues with underscores
        const uniqueSubHeadPairs = [];
        const seenPairs = new Set();
        flatItems.forEach(item => {
            if (item.head_id && item.sub_head_id) {
                const pairKey = `${item.head_id}|||${item.sub_head_id}`; // Use ||| as delimiter that won't appear in IDs
                if (!seenPairs.has(pairKey)) {
                    seenPairs.add(pairKey);
                    uniqueSubHeadPairs.push({
                        head_id: item.head_id,
                        sub_head_id: item.sub_head_id,
                        key: `${item.head_id}_${item.sub_head_id}` // Keep original key format for map storage
                    });
                }
            }
        });

        // Fetch all sub_heads options in parallel (without showing loader for each)
        const subHeadPromises = uniqueHeadIds.map(async (headId) => {
            if (headId && !subHeadsOptionsMap[headId]) {
                return await fetchSubHeads(headId, -1, false);
            }
            return subHeadsOptionsMap[headId] || [];
        });

        // Fetch all details options in parallel (without showing loader for each)
        const detailsPromises = uniqueSubHeadPairs.map(async (pair) => {
            const { head_id, sub_head_id, key } = pair;

            if (head_id && sub_head_id && !detailsOptionsMap[key]) {
                const result = await fetchDetails(head_id, sub_head_id, -1, false);
                return result;
            }
            return detailsOptionsMap[key] || [];
        });

        // Wait for all fetches to complete and build maps from fetched data
        let fetchedSubHeadsMap = {};
        let fetchedDetailsMap = {};
        let fetchedSizeOptionsMap = {}; // Track fetched size options separately
        try {
            const [fetchedSubHeads, fetchedDetailsResults] = await Promise.all([
                Promise.all(subHeadPromises),
                Promise.all(detailsPromises)
            ]);

            // Build subHeads map from fetched data
            fetchedSubHeads.forEach((options, index) => {
                const headId = uniqueHeadIds[index];
                if (headId && options && options.length > 0) {
                    fetchedSubHeadsMap[headId] = options;
                }
            });

            // Build details map from fetched data
            fetchedDetailsResults.forEach((options, index) => {
                const pair = uniqueSubHeadPairs[index];
                const key = pair?.key;
                
                if (key && options && Array.isArray(options) && options.length > 0) {
                    fetchedDetailsMap[key] = options;
                }
            });
            
            // Fetch size options for all pairs by calling the API directly
            // This is needed because fetchDetails updates state asynchronously
            const sizeOptionsPromises = uniqueSubHeadPairs.map(async (pair) => {
                const { head_id, sub_head_id, key } = pair;
                if (!head_id || !sub_head_id) return {};
                
                try {
                    const response = await getDropdownDataAPI('item', head_id, sub_head_id);
                    if (response.error) return {};
                    
                    const headsData = response.data?.data?.data || [];
                    let itemsData = [];
                    
                    for (const headData of headsData) {
                        if (headData.head_id === head_id || headData._id === head_id) {
                            const subHeads = headData.sub_heads || [];
                            for (const subHead of subHeads) {
                                if (subHead.sub_head_id === sub_head_id || subHead._id === sub_head_id) {
                                    itemsData = subHead.items || [];
                                    break;
                                }
                            }
                            break;
                        }
                    }
                    
                    // Group items by detail value to collect all unique sizes
                    const detailGroups = {};
                    itemsData.forEach((item) => {
                        const detailValue = item.details || item.name || item.item_id || item._id;
                        const itemSize = item.size;
                        
                        if (!detailGroups[detailValue]) {
                            detailGroups[detailValue] = [];
                        }
                        if (itemSize) {
                            detailGroups[detailValue].push(itemSize);
                        }
                    });
                    
                    // Create size options map for this key
                    const sizeMap = {};
                    Object.keys(detailGroups).forEach((detailValue) => {
                        const sizeKey = `${key}_${detailValue}`;
                        const uniqueSizes = [...new Set(detailGroups[detailValue])];
                        
                        if (uniqueSizes.length > 0) {
                            sizeMap[sizeKey] = uniqueSizes.map(size => ({
                                value: size,
                                label: size
                            }));
                        }
                    });
                    
                    return sizeMap;
                } catch (error) {
                    console.error(`Error fetching size options for ${key}:`, error);
                    return {};
                }
            });
            
            // Wait for all size options to be fetched
            const fetchedSizeOptionsResults = await Promise.all(sizeOptionsPromises);
            fetchedSizeOptionsResults.forEach((sizeMap) => {
                Object.assign(fetchedSizeOptionsMap, sizeMap);
            });
        } catch (error) {
            console.error('Error fetching dropdown options during pre-fill:', error);
            // Continue with pre-fill even if some fetches fail
        }

        // Update state maps first to ensure they're available for dropdowns
        if (Object.keys(fetchedSubHeadsMap).length > 0) {
            setSubHeadsOptionsMap(prev => ({ ...prev, ...fetchedSubHeadsMap }));
        }
        if (Object.keys(fetchedDetailsMap).length > 0) {
            setDetailsOptionsMap(prev => ({ ...prev, ...fetchedDetailsMap }));
        }
        if (Object.keys(fetchedSizeOptionsMap).length > 0) {
            setSizeOptionsMap(prev => ({ ...prev, ...fetchedSizeOptionsMap }));
        }

        // Note: sizeOptionsMap is updated by fetchDetails as a side effect
        // We need to wait a bit for the state to update, or use the current state
        // Since fetchDetails updates sizeOptionsMap via setSizeOptionsMap, and we're calling it in parallel,
        // the state should be updated by the time we use it (especially with the setTimeout delay)

        // Merge fetched maps with existing state maps (use fetched maps first as they're most recent)
        // Prioritize fetched maps over state maps since they're the most recent data
        // Use a callback to get the latest state, but for immediate use, merge with current state
        const allSubHeadsMap = { ...subHeadsOptionsMap, ...fetchedSubHeadsMap };
        const allDetailsMap = { ...fetchedDetailsMap, ...detailsOptionsMap };
        // Merge fetched size options with existing state (fetched first, then state)
        const allSizeOptionsMap = { ...fetchedSizeOptionsMap, ...sizeOptionsMap };
        // Determine miscellaneous status early for each item
        const isMiscItem = (item) => {
            return (!item.head_id && !item.sub_head_id) ||
                (item.head_id && item.head_id.toLowerCase() === "miscellaneous");
        };

        const resolveInvoiceValue = (invoiceValue, fallbackValue) => {
            if (invoiceValue === null || invoiceValue === undefined || invoiceValue === "") {
                return fallbackValue;
            }
            return invoiceValue;
        };
        const asText = (value) => {
            if (value === null || value === undefined || value === "") return "";
            return value.toString();
        };

        // Find matching values from dropdown options
        const preFilledRows = flatItems.map((item, index) => {
            const isMisc = isMiscItem(item);

            // Find matching head value from headsOptions
            let matchingHead = null;

            // For miscellaneous items, try to find "Miscellaneous" option
            if (isMisc) {
                matchingHead = headsOptions.find(opt => 
                    opt.head_id?.toLowerCase() === "miscellaneous" ||
                    opt.value?.toLowerCase() === "miscellaneous" ||
                    opt.label?.toLowerCase() === "miscellaneous"
                );
            }

            // First try exact head_id match
            if (!matchingHead && item.head_id) {
                matchingHead = headsOptions.find(opt => opt.head_id === item.head_id);
            }

            // Then try exact value match
            if (!matchingHead && item.head) {
                matchingHead = headsOptions.find(opt => opt.value === item.head);
            }

            // Then try exact label match
            if (!matchingHead && item.head) {
                matchingHead = headsOptions.find(opt => opt.label === item.head);
            }

            // Then try case-insensitive match
            if (!matchingHead && item.head) {
                matchingHead = headsOptions.find(opt =>
                    opt.value?.toLowerCase() === item.head?.toLowerCase() ||
                    opt.label?.toLowerCase() === item.head?.toLowerCase() ||
                    opt.head_id?.toLowerCase() === item.head_id?.toLowerCase()
                );
            }

            // Use matching head's value if found, otherwise try to use head_id to find a match
            // If still no match, use the item's head value or head_id as fallback
            let headDropdownValue = matchingHead?.value;
            if (!headDropdownValue && item.head_id) {
                // Try to find by head_id one more time with different matching
                const headById = headsOptions.find(opt => 
                    opt.head_id === item.head_id ||
                    opt.head_id?.toLowerCase() === item.head_id?.toLowerCase()
                );
                headDropdownValue = headById?.value;
            }
            if (!headDropdownValue) {
                // Use item's head value if available, otherwise try to construct from head_id
                headDropdownValue = item.head || (item.head_id ? item.head_id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "");
            }
            if (!headDropdownValue && isMisc) {
                headDropdownValue = "Miscellaneous";
            }

            // For miscellaneous items, skip sub_head and details matching
            if (isMisc) {
                // Determine if head should be shown (blank if same as previous)
                const prevItem = index > 0 ? flatItems[index - 1] : null;
                const showHead = index === 0 || !prevItem || (item.head_id !== prevItem.head_id);

                const invoiceSizeValue = resolveInvoiceValue(item.invoice_size, item.size);
                const invoiceQuantityValue = resolveInvoiceValue(item.invoice_quantity, item.qty);
                const invoicePriceValue = resolveInvoiceValue(item.invoice_price, item.benchmark_price);

                return {
                    id: `prefilled-row-${Date.now()}-${index}`,
                    heads: headDropdownValue,
                    head_id: "miscellaneous",
                    subHeads: item.sub_head || "",
                    sub_head_id: "",
                    details: item.details || "",
                    item_id: null,
                    size: item.size || "",
                    quantity: item.qty || "",
                    price: item.benchmark_price?.toString() || "",
                    invoiceSize: invoiceSizeValue || "",
                    invoiceQuantity: asText(invoiceQuantityValue),
                    invoicePrice: asText(invoicePriceValue),
                    remarks: item.remarks || "",
                    description: item.description || "",
                    isMiscellaneous: true,
                    showHead: showHead,
                    showSubHead: false,
                    showDetails: false,
                };
            }

            // Find matching sub_head value from allSubHeadsMap (includes freshly fetched)
            const subHeadOptions = allSubHeadsMap[item.head_id] || [];
            let matchingSubHead = null;
            
            // Try exact sub_head_id match first
            if (item.sub_head_id) {
                matchingSubHead = subHeadOptions.find(opt => 
                    opt.sub_head_id === item.sub_head_id ||
                    opt.sub_head_id?.toLowerCase() === item.sub_head_id?.toLowerCase()
                );
            }
            
            // Then try exact value match
            if (!matchingSubHead && item.sub_head) {
                matchingSubHead = subHeadOptions.find(opt => opt.value === item.sub_head);
            }
            
            // Then try exact label match
            if (!matchingSubHead && item.sub_head) {
                matchingSubHead = subHeadOptions.find(opt => opt.label === item.sub_head);
            }
            
            // Then try case-insensitive match
            if (!matchingSubHead && item.sub_head) {
                matchingSubHead = subHeadOptions.find(opt =>
                    opt.value?.toLowerCase() === item.sub_head?.toLowerCase() ||
                    opt.label?.toLowerCase() === item.sub_head?.toLowerCase()
                );
            }
            
            // Use matching sub_head's value if found, otherwise use item's sub_head value
            const subHeadDropdownValue = matchingSubHead?.value || item.sub_head || "";

            // Find matching details value from allDetailsMap (includes freshly fetched)
            const detailsKey = `${item.head_id}_${item.sub_head_id}`;
            let detailsOptions = allDetailsMap[detailsKey] || [];

            // If details options are empty, try to get from fetched map first, then state
            if (detailsOptions.length === 0) {
                if (fetchedDetailsMap[detailsKey] && fetchedDetailsMap[detailsKey].length > 0) {
                    detailsOptions = fetchedDetailsMap[detailsKey];
                } else {
                    if (detailsOptionsMap[detailsKey]) {
                        detailsOptions = detailsOptionsMap[detailsKey];
                    }
                }
            }

            // Try multiple matching strategies for details
            let matchingDetail = null;

            // First try exact item_id match (most reliable) - handle type conversion
            if (item.item_id && detailsOptions.length > 0) {
                const itemIdStr = String(item.item_id);
                matchingDetail = detailsOptions.find(opt => {
                    const optIdStr = String(opt.item_id || '');
                    const matches = optIdStr === itemIdStr || opt.item_id === item.item_id;
                    return matches;
                });
            }

            // Then try exact value match (normalize whitespace)
            if (!matchingDetail && item.details && detailsOptions.length > 0) {
                const normalizedDetails = item.details?.trim();
                matchingDetail = detailsOptions.find(opt => {
                    const optValue = opt.value?.trim();
                    const optLabel = opt.label?.trim();
                    const matches = optValue === normalizedDetails || optLabel === normalizedDetails;
                    return matches;
                });
            }

            // Then try case-insensitive match (with trim)
            if (!matchingDetail && item.details && detailsOptions.length > 0) {
                const itemDetailsTrimmed = item.details?.trim();
                matchingDetail = detailsOptions.find(opt => {
                    const optValueTrimmed = opt.value?.trim();
                    const optLabelTrimmed = opt.label?.trim();
                    const matches = (
                        optValueTrimmed?.toLowerCase() === itemDetailsTrimmed?.toLowerCase() ||
                        optLabelTrimmed?.toLowerCase() === itemDetailsTrimmed?.toLowerCase()
                    );
                    return matches;
                });
            }

            // Finally try partial match (contains) - less reliable but as fallback
            if (!matchingDetail && item.details && detailsOptions.length > 0) {
                const itemDetailsLower = item.details?.toLowerCase()?.trim();
                matchingDetail = detailsOptions.find(opt => {
                    const optValueLower = opt.value?.toLowerCase()?.trim();
                    const optLabelLower = opt.label?.toLowerCase()?.trim();
                    const matches = (
                        optValueLower?.includes(itemDetailsLower) ||
                        optLabelLower?.includes(itemDetailsLower) ||
                        itemDetailsLower?.includes(optValueLower) ||
                        itemDetailsLower?.includes(optLabelLower)
                    );
                    return matches;
                });
            }

            // Use matching detail's value if found, otherwise try to find by value/label match
            let detailsDropdownValue = matchingDetail?.value || "";
            let finalItemId = matchingDetail?.item_id || item.item_id || "";

            // If no match found but we have item.details, try to find it in options by value/label
            if (!detailsDropdownValue && item.details && detailsOptions.length > 0) {
                const fallbackMatch = detailsOptions.find(opt => {
                    const optValue = String(opt.value || '').trim();
                    const optLabel = String(opt.label || '').trim();
                    const itemDetails = String(item.details || '').trim();
                    return optValue === itemDetails || optLabel === itemDetails;
                });
                if (fallbackMatch) {
                    detailsDropdownValue = fallbackMatch.value || item.details || "";
                    finalItemId = fallbackMatch.item_id || item.item_id || "";
                } else {
                    // Still use the item's details value even if not found in options
                    // This allows editing to work even if dropdown options have changed
                    detailsDropdownValue = item.details || "";
                }
            } else if (!detailsDropdownValue) {
                // Use item's details value as fallback
                detailsDropdownValue = item.details || "";
            }

            // Always include the row even if details don't match options
            // This is important for editing existing quotations where options might have changed

            // Match size with available size options
            // Size options are keyed by: ${head_id}_${sub_head_id}_${detailValue}
            // The detailValue in the key is the original detail value from the API response
            // We need to try both the original item.details and the matched detailsDropdownValue
            let sizeDropdownValue = item.size || "";
            if (item.head_id && item.sub_head_id && item.size) {
                // Try to get size options using the original details value first (as stored in API)
                // This is the value used when fetchDetails stores size options
                const originalDetailsKey = `${item.head_id}_${item.sub_head_id}_${item.details}`;
                let availableSizes = allSizeOptionsMap[originalDetailsKey] || [];
                
                // If not found, try with the matched detailsDropdownValue (in case dropdown value differs)
                if (availableSizes.length === 0 && detailsDropdownValue && detailsDropdownValue !== item.details) {
                    const matchedDetailsKey = `${item.head_id}_${item.sub_head_id}_${detailsDropdownValue}`;
                    availableSizes = allSizeOptionsMap[matchedDetailsKey] || [];
                }
                
                // Try to find matching size in options
                if (availableSizes.length > 0) {
                    const matchingSize = availableSizes.find(opt => 
                        opt.value === item.size ||
                        opt.label === item.size ||
                        String(opt.value || '').toLowerCase()?.trim() === String(item.size || '').toLowerCase()?.trim() ||
                        String(opt.label || '').toLowerCase()?.trim() === String(item.size || '').toLowerCase()?.trim()
                    );
                    
                    if (matchingSize) {
                        sizeDropdownValue = matchingSize.value || item.size || "";
                    } else {
                        // If size doesn't match any option, still use the item's size
                        // This allows editing to work even if size options have changed
                        sizeDropdownValue = item.size || "";
                    }
                } else {
                    // If no size options available yet, use the item's size directly
                    // The options might be fetched later, but we should still set the value
                    // The dropdown should be able to handle the value even if it's not in options initially
                    sizeDropdownValue = item.size || "";
                }
            }

            // Determine if head/subhead/details should be shown (blank if same as previous)
            const prevItem = index > 0 ? flatItems[index - 1] : null;
            const showHead = index === 0 || !prevItem || (item.head_id !== prevItem.head_id);
            const showSubHead = index === 0 || !prevItem || (item.sub_head_id !== prevItem.sub_head_id);
            const showDetails = index === 0 || !prevItem || (finalItemId !== (prevItem?.item_id || ""));

            const invoiceSizeValue = resolveInvoiceValue(item.invoice_size, sizeDropdownValue);
            const invoiceQuantityValue = resolveInvoiceValue(item.invoice_quantity, item.qty);
            const invoicePriceValue = resolveInvoiceValue(item.invoice_price, item.benchmark_price);

            return {
                id: `prefilled-row-${Date.now()}-${index}`,
                heads: headDropdownValue,
                head_id: item.head_id,
                subHeads: subHeadDropdownValue,
                sub_head_id: item.sub_head_id,
                details: detailsDropdownValue,
                item_id: finalItemId,
                size: sizeDropdownValue,
                quantity: item.qty || "",
                price: item.benchmark_price?.toString() || "",
                invoiceSize: invoiceSizeValue || "",
                invoiceQuantity: asText(invoiceQuantityValue),
                invoicePrice: asText(invoicePriceValue),
                remarks: item.remarks || "",
                description: "",
                isMiscellaneous: false,
                showHead: showHead,
                showSubHead: showSubHead,
                showDetails: showDetails,
            };
        }).filter(row => row !== null && row !== undefined); // Filter out any null/undefined rows

        // Filter out any invalid rows (rows that should be skipped)
        const validPreFilledRows = preFilledRows.filter((row) => {
            // Always include miscellaneous rows
            if (row.isMiscellaneous) {
                return true;
            }
            // For non-miscellaneous rows, include if they have at least head_id
            return row.head_id && (row.details || row.item_id || row.heads);
        });

        // Use setTimeout to ensure state updates are reflected before setting rows
        // This ensures the dropdown has access to the options when it renders
        // Increased delay to ensure React has processed state updates for detailsOptionsMap
        setTimeout(() => {
            // Update remaining rows count based on valid pre-filled rows
            const finalRemainingRows = Array.from(
                { length: isAddInvoiceFlow ? 0 : Math.max(0, 10 - validPreFilledRows.length) },
                (_, index) => ({
                id: `empty-row-${Date.now()}-${index}`,
                heads: "",
                head_id: "",
                subHeads: "",
                sub_head_id: "",
                details: "",
                item_id: "",
                size: "",
                quantity: "",
                price: "",
                invoiceSize: "",
                invoiceQuantity: "",
                invoicePrice: "",
                remarks: "",
                description: "",
                isMiscellaneous: false,
                showHead: true,
                showSubHead: true,
                showDetails: true,
            }));
            const nextRows = [...validPreFilledRows, ...finalRemainingRows];
            setRows(nextRows);
            const nextErrors = {};
            nextRows.forEach((row) => {
                const rowErrors = buildRowZeroErrors(row);
                if (rowErrors.quantity || rowErrors.price || rowErrors.invoiceQuantity || rowErrors.invoicePrice) {
                    nextErrors[row.id] = rowErrors;
                }
            });
            setFieldErrors(nextErrors);
        }, 300);
    };

    // Fetch heads dropdown data
    const fetchHeads = async () => {
        setLoadingHeads(true);
        LoaderAnimation.show();
        try {
            const response = await getDropdownDataAPI('head');
            if (response.error) {
                toaster(response.message || 'Failed to fetch heads', 'error', 'error');
                return;
            }
            const headsData = response.data?.data?.data || [];
            const options = headsData.map((item) => ({
                value: item.head || item.name || item._id,
                label: item.head || item.name || item._id,
                head_id: item.head_id || item.head || item._id // Use head_id field or head value for API calls
            }));
            setHeadsOptions(options);
        } catch (error) {
            console.error('Error fetching heads:', error);
            toaster('Failed to fetch heads', 'error', 'error');
        } finally {
            setLoadingHeads(false);
            LoaderAnimation.hide();
        }
    };

    // Fetch sub_heads based on selected head_id
    const fetchSubHeads = async (head_id, rowIndex, showLoader = true) => {
        if (!head_id) {
            return [];
        }
        // Skip if already loaded
        if (subHeadsOptionsMap[head_id] && subHeadsOptionsMap[head_id].length > 0) {
            return subHeadsOptionsMap[head_id];
        }
        // Prevent duplicate calls
        if (loadingSubHeads[head_id]) {
            return [];
        }
        setLoadingSubHeads(prev => ({ ...prev, [head_id]: true }));
        if (showLoader) {
            LoaderAnimation.show();
        }
        try {
            const response = await getDropdownDataAPI('sub_heads', head_id);
            if (response.error) {
                if (showLoader) {
                    toaster(response.message || 'Failed to fetch sub heads', 'error', 'error');
                }
                return [];
            }
            const subHeadsData = response.data?.data?.data || [];
            const options = subHeadsData.map((item) => ({
                value: item.sub_head || item.name || item.sub_head_id || item._id,
                label: item.sub_head || item.name || item.sub_head_id || item._id,
                sub_head_id: item.sub_head_id || item.sub_head || item._id // Use sub_head_id field or sub_head value for API calls
            }));
            setSubHeadsOptionsMap(prev => ({
                ...prev,
                [head_id]: options
            }));
            return options;
        } catch (error) {
            console.error('Error fetching sub heads:', error);
            if (showLoader) {
                toaster('Failed to fetch sub heads', 'error', 'error');
            }
            return [];
        } finally {
            setLoadingSubHeads(prev => ({ ...prev, [head_id]: false }));
            if (showLoader) {
                LoaderAnimation.hide();
            }
        }
    };

    // Fetch items/details based on selected head_id and sub_head_id
    const fetchDetails = async (head_id, sub_head_id, rowIndex, showLoader = true) => {
        if (!head_id || !sub_head_id) {
            return [];
        }
        const key = `${head_id}_${sub_head_id}`;
        // Skip if already loaded
        if (detailsOptionsMap[key] && detailsOptionsMap[key].length > 0) {
            return detailsOptionsMap[key];
        }
        // Prevent duplicate calls
        if (loadingDetails[key]) {
            return [];
        }
        setLoadingDetails(prev => ({ ...prev, [key]: true }));
        if (showLoader) {
            LoaderAnimation.show();
        }
        try {
            const response = await getDropdownDataAPI('item', head_id, sub_head_id);
            if (response.error) {
                if (showLoader) {
                    toaster(response.message || 'Failed to fetch details', 'error', 'error');
                }
                return [];
            }
            // Response structure: data array contains head objects, each with sub_heads array
            const headsData = response.data?.data?.data || [];

            let itemsData = [];
            let matchedHead = null;
            let matchedSubHead = null;

            // Find the matching head and sub_head, then extract items
            for (const headData of headsData) {
                const headMatches = headData.head_id === head_id || headData._id === head_id;
                if (headMatches) {
                    matchedHead = headData;
                    const subHeads = headData.sub_heads || [];

                    for (const subHead of subHeads) {
                        const subHeadMatches = subHead.sub_head_id === sub_head_id || subHead._id === sub_head_id;
                        if (subHeadMatches) {
                            matchedSubHead = subHead;
                            itemsData = subHead.items || [];
                            break;
                        }
                    }
                    break;
                }
            }

            const options = itemsData.map((item) => ({
                value: item.details || item.name || item.item_id || item._id,
                label: item.details || item.name || item.item_id || item._id,
                item_id: item.item_id || item.details || item._id,
                size: item.size || null // Store size with each item
            }));

            // Extract unique sizes for each item and store in sizeOptionsMap
            const newSizeOptionsMap = { ...sizeOptionsMap };
            const newItemSizeMap = { ...itemSizeMap };
            
            // Group items by detail value to collect all unique sizes for each detail
            const detailGroups = {};
            itemsData.forEach((item) => {
                const itemId = item.item_id || item.details || item._id;
                const itemSize = item.size;
                const detailValue = item.details || item.name || item.item_id || item._id;
                
                // Store item_id -> size mapping
                if (itemSize) {
                    newItemSizeMap[itemId] = itemSize;
                }
                
                // Group by detail value
                if (!detailGroups[detailValue]) {
                    detailGroups[detailValue] = [];
                }
                if (itemSize) {
                    detailGroups[detailValue].push(itemSize);
                }
            });
            
            // Create size options for each detail group
            Object.keys(detailGroups).forEach((detailValue) => {
                const sizeKey = `${key}_${detailValue}`;
                const uniqueSizes = [...new Set(detailGroups[detailValue])]; // Get unique sizes
                
                if (uniqueSizes.length > 0) {
                    newSizeOptionsMap[sizeKey] = uniqueSizes.map(size => ({
                        value: size,
                        label: size
                    }));
                }
            });
            
            setSizeOptionsMap(newSizeOptionsMap);
            setItemSizeMap(newItemSizeMap);
            setDetailsOptionsMap(prev => ({
                ...prev,
                [key]: options
            }));
            return options;
        } catch (error) {
            console.error('Error fetching details:', error);
            if (showLoader) {
                toaster('Failed to fetch details', 'error', 'error');
            }
            return [];
        } finally {
            setLoadingDetails(prev => ({ ...prev, [key]: false }));
            if (showLoader) {
                LoaderAnimation.hide();
            }
        }
    };

    // Initialize with 10 rows
    const initialRows = Array.from({ length: isAddInvoiceFlow ? 0 : 10 }, (_, index) => ({
        id: `row-${Date.now()}-${index}`, // Unique ID for each row
        heads: "",
        head_id: "",
        subHeads: "",
        sub_head_id: "",
        details: "",
        item_id: "",
        size: "",
        quantity: "",
        price: "",
        invoiceSize: "",
        invoiceQuantity: "",
        invoicePrice: "",
        remarks: "",
        description: "",
        isMiscellaneous: false,
        showHead: true, // Flag to show/hide head dropdown
        showSubHead: true, // Flag to show/hide subhead dropdown
        showDetails: true, // Flag to show/hide details dropdown
    }));

    const [rows, setRows] = useState(initialRows);
    const [fieldErrors, setFieldErrors] = useState({});
    const [addRowCount, setAddRowCount] = useState("5"); // Default to 5 rows

    // Dropdown options - will be populated from API
    const [headsOptions, setHeadsOptions] = useState([]);
    const [subHeadsOptionsMap, setSubHeadsOptionsMap] = useState({}); // Map of head_id -> sub_heads options
    const [detailsOptionsMap, setDetailsOptionsMap] = useState({}); // Map of head_id_sub_head_id -> details options
    const [sizeOptionsMap, setSizeOptionsMap] = useState({}); // Map of head_id_sub_head_id_item_id -> size options
    const [itemSizeMap, setItemSizeMap] = useState({}); // Map of item_id -> size for quick lookup

    // Loading states for dropdowns
    const [loadingHeads, setLoadingHeads] = useState(false);
    const [loadingSubHeads, setLoadingSubHeads] = useState({}); // Map of head_id -> loading state
    const [loadingDetails, setLoadingDetails] = useState({}); // Map of head_id_sub_head_id -> loading state

    // State for quotations data and edit request modal
    const [quotationsData, setQuotationsData] = useState(null);
    const [showEditRequestModal, setShowEditRequestModal] = useState(false);
    const [editRequestReason, setEditRequestReason] = useState("");
    const [isRequestingEdit, setIsRequestingEdit] = useState(false);
    const [isAdminEditApproved, setIsAdminEditApproved] = useState(true);
    const [showConfirmSubmitModal, setShowConfirmSubmitModal] = useState(false);
    const [isSubmittingQuotation, setIsSubmittingQuotation] = useState(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [showConfirmDraftModal, setShowConfirmDraftModal] = useState(false);
    const [showPendingRequestModal, setShowPendingRequestModal] = useState(false);

    // State for vendor details from API
    const [vendorGstNumber, setVendorGstNumber] = useState("");
    const [vendorLogo, setVendorLogo] = useState("");
    const [vendorCompanyName, setVendorCompanyName] = useState("");
    const [vendorEmail, setVendorEmail] = useState("");
    const [vendorName, setVendorName] = useState("");


    // Vendor details - GST and logo from API, name/email/company to be updated later
    const vendorDetails = {
        name: vendorName || "-", // From API: travel_partner_details.contactDetails.contactName (matched with user email when is_empty is true)
        email: vendorEmail || "-", // From API: travel_partner_details.contactDetails (matched with user email when is_empty is true)
        gstNo: vendorGstNumber || "-", // From API: travel_partner_details.gst_number
        company: vendorCompanyName || "-", // From API: travel_partner_details.agency
        logo: vendorLogo // From API: travel_partner_details.logo
    };
    console.log(vendorDetails.logo,'Logo in Quotation')
    // Fetch heads on component mount
    useEffect(() => {
        fetchHeads();
    }, []);

    // Track if quotations have been fetched to prevent multiple calls
    const [quotationsFetched, setQuotationsFetched] = useState(false);
    const [lastFetchedEventId, setLastFetchedEventId] = useState(null);

    // Get event ID from Redux or session storage (for refresh scenarios)
    const getEventId = () => {
        if (eventsDetail?._id) {
            return eventsDetail._id;
        }
        // Fallback to session storage on refresh
        const storedEvent = getEventFromStorage();
        return storedEvent?._id || null;
    };

    // Fetch quotations on component mount and pre-fill data
    useEffect(() => {
        const fetchQuotations = async () => {
            const eventId = getEventId();

            // Reset quotationsFetched if event_id changed (e.g., after refresh when Redux state loads)
            if (eventId && eventId !== lastFetchedEventId) {
                setQuotationsFetched(false);
                setLastFetchedEventId(eventId);
            }

            if (!eventId || headsOptions.length === 0) {
                return;
            }

            // If already fetched for this event, don't fetch again
            if (quotationsFetched && lastFetchedEventId === eventId) {
                return;
            }

            const travel_partner_id = getTravelPartnerId();
            if (!travel_partner_id) {
                // Don't set flag to true if travel_partner_id is missing - allow retry when eventsDetail loads
                // But only if we have event data from Redux (not just session storage)
                // This allows the effect to retry when Redux state loads
                if (eventsDetail?._id) {
                    // If Redux state is loaded but travel_partner_id is still missing, set flag to prevent infinite retries
                    setQuotationsFetched(true);
                }
                return;
            }
            setQuotationsFetched(true);
            setLastFetchedEventId(eventId);
            try {
                const response = await listQuotationsAPI(eventId, travel_partner_id);
                if (!response.error && response.data?.data?.data && response.data.data.data.length > 0) {
                    const quotation = response.data.data.data[0]; // Get first quotation
                    setQuotationsData(quotation);
                    setIsAdminEditApproved(quotation.is_admin_edit_approved !== false);

                    // Extract vendor details from travel_partner_details
                    if (quotation.travel_partner_details) {
                        setVendorGstNumber(quotation.travel_partner_details.gst_number || "");
                        setVendorLogo(quotation.travel_partner_details.logo || "");
                        setVendorCompanyName(quotation.travel_partner_details.agency || "");

                        // If is_empty is true, match user email with contactDetails
                        if (quotation.is_empty === true && quotation.travel_partner_details.contactDetails) {
                            const userInfo = getUserInfo();
                            const userEmail = userInfo?.email;
                            if (userEmail && Array.isArray(quotation.travel_partner_details.contactDetails)) {
                                // Find the contact that matches user's email
                                const matchedContact = quotation.travel_partner_details.contactDetails.find(contact => {
                                    if (Array.isArray(contact.emailId)) {
                                        return contact.emailId.includes(userEmail);
                                    }
                                    return contact.emailId === userEmail;
                                });

                                // Set the matched email and name (use first email from the matched contact's emailId array)
                                if (matchedContact) {
                                    const matchedEmail = Array.isArray(matchedContact.emailId)
                                        ? matchedContact.emailId[0]
                                        : matchedContact.emailId;
                                    setVendorEmail(matchedEmail || "");
                                    setVendorName(matchedContact.contactName || "");
                                } else {
                                    setVendorEmail("");
                                    setVendorName("");
                                }
                            } else {
                                setVendorEmail("");
                                setVendorName("");
                            }
                        } else if (quotation.travel_partner_email && quotation.travel_partner_details.contactDetails) {
                            // If travel_partner_email exists (quotation data present), match it with contactDetails
                            const travelPartnerEmail = quotation.travel_partner_email;
                            if (Array.isArray(quotation.travel_partner_details.contactDetails)) {
                                // Find the contact that matches travel_partner_email
                                const matchedContact = quotation.travel_partner_details.contactDetails.find(contact => {
                                    if (Array.isArray(contact.emailId)) {
                                        return contact.emailId.includes(travelPartnerEmail);
                                    }
                                    return contact.emailId === travelPartnerEmail;
                                });

                                // Set the travel_partner_email and matched contact's name
                                if (matchedContact) {
                                    setVendorEmail(travelPartnerEmail || "");
                                    setVendorName(matchedContact.contactName || "");
                                } else {
                                    setVendorEmail(travelPartnerEmail || "");
                                    setVendorName("");
                                }
                            } else {
                                setVendorEmail(quotation.travel_partner_email || "");
                                setVendorName("");
                            }
                        } else {
                            setVendorEmail("");
                            setVendorName("");
                        }
                    }

                    // Pre-fill rows with quotation data (async function)
                    await prefillQuotationData(quotation);
                } else {
                    // No quotations found, reset data
                    setQuotationsData(null);
                    setVendorGstNumber("");
                    setVendorLogo("");
                    setVendorCompanyName("");
                    setVendorEmail("");
                    setVendorName("");
                }
            } catch (error) {
                console.error('Error fetching quotations:', error);
                setQuotationsFetched(false); // Reset on error to allow retry
                setLastFetchedEventId(null);
            } finally {
                // Ensure loader is hidden even if prefillQuotationData fails
                LoaderAnimation.hide();
            }
        };
        fetchQuotations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventsDetail?._id, eventsDetail?.agency, headsOptions.length]);

    // Cleanup: ensure loader is hidden when component unmounts
    useEffect(() => {
        return () => {
            LoaderAnimation.hide();
        };
    }, []);

    // Get size options based on selected head, subhead, and details
    const getSizeOptions = (head_id, sub_head_id, details) => {
        if (!head_id || !sub_head_id || !details) {
            return [];
        }
        const key = `${head_id}_${sub_head_id}`;
        const sizeKey = `${key}_${details}`;
        return sizeOptionsMap[sizeKey] || [];
    };

    const normalizeText = (value) => (value ?? "").toString().trim();
    const isZeroValue = (value) => {
        const normalized = normalizeText(value).replace(/,/g, "");
        if (!normalized) return false;
        const parsed = Number(normalized);
        return Number.isFinite(parsed) && parsed === 0;
    };

    const buildRowZeroErrors = (row) => ({
        quantity: isZeroValue(row?.quantity),
        price: isZeroValue(row?.price),
        invoiceQuantity: isZeroValue(row?.invoiceQuantity),
        invoicePrice: isZeroValue(row?.invoicePrice)
    });

    const validateZeroValues = () => {
        const nextErrors = {};
        rows.forEach((row) => {
            const rowErrors = buildRowZeroErrors(row);
            if (rowErrors.quantity || rowErrors.price || rowErrors.invoiceQuantity || rowErrors.invoicePrice) {
                nextErrors[row.id] = rowErrors;
            }
        });
        setFieldErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const addRow = (count = 1) => {
        const newRows = Array.from({ length: count }, (_, index) => ({
            id: `new-row-${Date.now()}-${index}`, // Unique ID for each row
            heads: "",
            head_id: "",
            subHeads: "",
            sub_head_id: "",
            details: "",
            item_id: "",
            size: "",
            quantity: "",
            price: "",
            invoiceSize: "",
            invoiceQuantity: "",
            invoicePrice: "",
            remarks: "",
            description: "",
            isMiscellaneous: false,
            showHead: true,
            showSubHead: true,
            showDetails: true,
        }));
        const updatedRows = [...rows, ...newRows];
        setRows(updatedRows);
    };

    // Add row count options for dropdown
    const addRowCountOptions = [
        { value: "1", label: "1" },
        { value: "5", label: "5" },
        { value: "10", label: "10" },
        { value: "25", label: "25" },
        { value: "50", label: "50" },
    ];

    const handleAddRows = () => {
        if (isQuotationReadOnly) {
            return;
        }
        if (addRowCount) {
            addRow(parseInt(addRowCount, 10));
        }
    };

    const removeRow = (index) => {
        if (isQuotationReadOnly) {
            return;
        }
        if (rows.length > 1) {
            const updatedRows = rows.filter((_, i) => i !== index);
            const removedRow = rows[index];
            setRows(updatedRows);
            if (removedRow?.id) {
                setFieldErrors((prev) => {
                    const next = { ...prev };
                    delete next[removedRow.id];
                    return next;
                });
            }
        }
    };

    // Add row with same head
    const addRowWithSameHead = (index) => {
        if (isQuotationReadOnly) {
            return;
        }
        const currentRow = rows[index];
        if (!currentRow.heads || !currentRow.head_id) {
            toaster('Please select a head first', 'error', 'error');
            return;
        }
        const newRow = {
            id: `same-head-row-${Date.now()}-${index}`, // Unique ID for each row
            heads: currentRow.heads,
            head_id: currentRow.head_id,
            subHeads: "",
            sub_head_id: "",
            details: "",
            item_id: "",
            size: "",
            quantity: "",
            price: "",
            invoiceSize: "",
            invoiceQuantity: "",
            invoicePrice: "",
            remarks: "",
            description: "",
            isMiscellaneous: currentRow.isMiscellaneous,
            showHead: false, // Hide head dropdown as it's same as previous
            showSubHead: currentRow.isMiscellaneous ? false : true, // For Miscellaneous, hide subhead (it's text input)
            showDetails: currentRow.isMiscellaneous ? false : true, // For Miscellaneous, hide details (it's text input)
        };
        const newRows = [...rows];
        newRows.splice(index + 1, 0, newRow);
        setRows(newRows);
        // Fetch sub_heads if head_id exists
        if (currentRow.head_id && !currentRow.isMiscellaneous) {
            fetchSubHeads(currentRow.head_id, index + 1);
        }
    };

    // Add row with same head and subhead
    const addRowWithSameHeadSubHead = (index) => {
        if (isQuotationReadOnly) {
            return;
        }
        const currentRow = rows[index];
        // For Miscellaneous, only check head_id and subHeads (text field)
        if (currentRow.isMiscellaneous) {
            if (!currentRow.heads || !currentRow.head_id || !currentRow.subHeads) {
                toaster('Please select a head and enter subhead first', 'error', 'error');
                return;
            }
        } else {
            if (!currentRow.heads || !currentRow.head_id || !currentRow.subHeads || !currentRow.sub_head_id) {
                toaster('Please select a head and subhead first', 'error', 'error');
                return;
            }
        }
        const previousRow = rows[index - 1];
        // When adding with same subhead, head should also be blank (same as current row)
        const shouldHideHead = previousRow && previousRow.head_id === currentRow.head_id;
        const newRow = {
            id: `same-head-subhead-row-${Date.now()}-${index}`, // Unique ID for each row
            heads: currentRow.heads,
            head_id: currentRow.head_id,
            subHeads: currentRow.subHeads,
            sub_head_id: currentRow.sub_head_id,
            details: "",
            item_id: "",
            size: "",
            quantity: "",
            price: "",
            invoiceSize: "",
            invoiceQuantity: "",
            invoicePrice: "",
            remarks: "",
            description: "",
            isMiscellaneous: currentRow.isMiscellaneous,
            showHead: false, // Always hide head when adding with same subhead (subhead implies same head)
            showSubHead: false, // Hide subhead dropdown as it's same as previous
            showDetails: currentRow.isMiscellaneous ? false : true, // For Miscellaneous, hide details (it's text input)
        };
        const newRows = [...rows];
        newRows.splice(index + 1, 0, newRow);
        setRows(newRows);
        // Fetch details if head_id and sub_head_id exist
        if (currentRow.head_id && currentRow.sub_head_id && !currentRow.isMiscellaneous) {
            fetchDetails(currentRow.head_id, currentRow.sub_head_id, index + 1);
        }
    };

    // Add row with same head, subhead, and details
    const addRowWithSameDetails = (index) => {
        const currentRow = rows[index];
        // For Miscellaneous, only check head_id, subHeads (text), and details (text)
        if (currentRow.isMiscellaneous) {
            if (!currentRow.heads || !currentRow.head_id || !currentRow.subHeads || !currentRow.details) {
                toaster('Please select head, enter subhead, and details first', 'error', 'error');
                return;
            }
        } else {
            if (!currentRow.heads || !currentRow.head_id || !currentRow.subHeads || !currentRow.sub_head_id || !currentRow.details) {
                toaster('Please select head, subhead, and details first', 'error', 'error');
                return;
            }
        }
        const previousRow = rows[index - 1];
        // When adding with same details, head and subhead should also be blank (details belong to specific head+subhead)
        const shouldHideHead = previousRow && previousRow.head_id === currentRow.head_id;
        const shouldHideSubHead = previousRow && previousRow.sub_head_id === currentRow.sub_head_id;
        const newRow = {
            id: `same-details-row-${Date.now()}-${index}`, // Unique ID for each row
            heads: currentRow.heads,
            head_id: currentRow.head_id,
            subHeads: currentRow.subHeads,
            sub_head_id: currentRow.sub_head_id,
            details: currentRow.details,
            item_id: currentRow.item_id,
            size: "",
            quantity: "",
            price: "",
            invoiceSize: "",
            invoiceQuantity: "",
            invoicePrice: "",
            remarks: "",
            description: "",
            isMiscellaneous: currentRow.isMiscellaneous,
            showHead: false, // Always hide head when adding with same details (details imply same head+subhead)
            showSubHead: false, // Always hide subhead when adding with same details
            showDetails: false, // Hide details dropdown as it's same as previous
        };
        const newRows = [...rows];
        newRows.splice(index + 1, 0, newRow);
        setRows(newRows);
    };

    const updateRow = (index, field, value) => {
        if (isQuotationReadOnly) {
            return;
        }
        if (isAddInvoiceFlow) {
            const invoiceEditableFields = new Set(["invoiceSize", "invoiceQuantity", "invoicePrice"]);
            if (!invoiceEditableFields.has(field)) {
                return;
            }
        }
        const updatedRows = rows.map((row, i) => {
            if (i === index) {
                const updatedRow = { ...row, [field]: value };
                // When Heads changes
                if (field === "heads") {
                    const selectedHead = headsOptions.find(opt =>
                        opt.value === value ||
                        opt.value?.toLowerCase() === value?.toLowerCase() ||
                        opt.head_id?.toLowerCase() === value?.toLowerCase()
                    );
                    const head_id = selectedHead?.head_id || value || "";
                    const isMisc = head_id?.toLowerCase() === "miscellaneous" || value?.toLowerCase() === "miscellaneous";

                    updatedRow.head_id = isMisc ? "miscellaneous" : head_id; // Use lowercase for payload
                    updatedRow.heads = value; // Keep original API value for dropdown
                    updatedRow.showHead = true;
                    updatedRow.isMiscellaneous = isMisc;

                    if (isMisc) {
                        updatedRow.subHeads = "";
                        updatedRow.sub_head_id = "";
                        updatedRow.details = "";
                        updatedRow.item_id = "";
                        updatedRow.size = "";
                        updatedRow.quantity = "";
                        updatedRow.price = "";
                        updatedRow.invoiceSize = "";
                        updatedRow.invoiceQuantity = "";
                        updatedRow.invoicePrice = "";
                        updatedRow.remarks = "";
                        updatedRow.description = "";
                        updatedRow.showSubHead = false;
                        updatedRow.showDetails = false;
                    } else {
                        updatedRow.description = "";
                        updatedRow.subHeads = undefined;
                        updatedRow.sub_head_id = "";
                        updatedRow.details = undefined;
                        updatedRow.item_id = "";
                        updatedRow.size = undefined;
                        updatedRow.quantity = "";
                        updatedRow.price = "";
                        updatedRow.invoiceSize = "";
                        updatedRow.invoiceQuantity = "";
                        updatedRow.invoicePrice = "";
                        updatedRow.remarks = "";
                        updatedRow.showSubHead = true;
                        updatedRow.showDetails = true;
                        if (head_id) {
                            fetchSubHeads(head_id, index);
                        }
                    }
                }
                // When SubHeads changes
                else if (field === "subHeads") {
                    const head_id = updatedRow.head_id;
                    const isMisc = updatedRow.isMiscellaneous;
                    
                    if (isMisc) {
                        // For Miscellaneous, just update the value as free text
                        updatedRow.subHeads = value;
                        updatedRow.sub_head_id = "";
                    } else {
                        const selectedSubHead = subHeadsOptionsMap[head_id]?.find(opt => opt.value === value);
                        const sub_head_id = selectedSubHead?.sub_head_id || value || "";
                        updatedRow.sub_head_id = sub_head_id;
                        updatedRow.showSubHead = true; // Show subhead dropdown when changed
                        // Clear dependent fields
                        updatedRow.details = undefined;
                        updatedRow.item_id = "";
                        updatedRow.size = undefined;
                        updatedRow.quantity = "";
                        updatedRow.price = "";
                        updatedRow.invoiceSize = "";
                        updatedRow.invoiceQuantity = "";
                        updatedRow.invoicePrice = "";
                        updatedRow.remarks = "";
                        updatedRow.showDetails = true;
                        // Fetch details for the selected head and sub_head
                        if (head_id && sub_head_id) {
                            fetchDetails(head_id, sub_head_id, index);
                        }
                    }
                }
                // When Details changes
                else if (field === "details") {
                    const head_id = updatedRow.head_id;
                    const isMisc = updatedRow.isMiscellaneous;
                    
                    if (isMisc) {
                        // For Miscellaneous, just update the value as free text
                        updatedRow.details = value;
                        updatedRow.item_id = "";
                    } else {
                        const sub_head_id = updatedRow.sub_head_id;
                        const key = `${head_id}_${sub_head_id}`;
                        const selectedDetail = detailsOptionsMap[key]?.find(opt => opt.value === value);
                        const item_id = selectedDetail?.item_id || value || "";
                        updatedRow.item_id = item_id;
                        updatedRow.showDetails = true; // Show details dropdown when changed
                        
                        // Reset dependent fields (size, quantity, price, remarks)
                        updatedRow.size = undefined;
                        updatedRow.quantity = "";
                        updatedRow.price = "";
                        updatedRow.invoiceSize = "";
                        updatedRow.invoiceQuantity = "";
                        updatedRow.invoicePrice = "";
                        updatedRow.remarks = "";
                    }
                }
                // When Size changes - don't reset other fields
                else if (field === "size") {
                    updatedRow.size = value;
                    // Don't reset quantity, price, remarks when size changes
                }
                return updatedRow;
            }
            return row;
        });
        setRows(updatedRows);
        const updatedRow = updatedRows[index];
        if (updatedRow?.id) {
            const rowErrors = buildRowZeroErrors(updatedRow);
            setFieldErrors((prev) => ({
                ...prev,
                [updatedRow.id]: rowErrors
            }));
        }
    };

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // Check if current user has quotation submit permission
    const canUserSubmitQuotation = () => {
        if (!quotationsData?.travel_partner_details?.contactDetails) {
            return false;
        }

        const userInfo = getUserInfo();
        const userEmail = userInfo?.email;
        
        if (!userEmail) {
            return false;
        }

        const contactDetails = quotationsData.travel_partner_details.contactDetails;
        if (!Array.isArray(contactDetails)) {
            return false;
        }

        // Find the contact that matches user's email
        const matchedContact = contactDetails.find(contact => {
            if (Array.isArray(contact.emailId)) {
                return contact.emailId.includes(userEmail);
            }
            return contact.emailId === userEmail;
        });

        // Return true only if contact is found and is_quotation_submit is true
        return matchedContact?.is_quotation_submit === true;
    };

    const getRowValuesForSubmit = (row) => {
        if (isAddInvoiceFlow) {
            return {
                size: row.invoiceSize ?? row.size ?? "",
                quantity: row.invoiceQuantity ?? row.quantity ?? "",
                price: row.invoicePrice ?? row.price ?? ""
            };
        }
        return {
            size: row.size ?? "",
            quantity: row.quantity ?? "",
            price: row.price ?? ""
        };
    };

    const handleUpload = () => {
        if (isQuotationReadOnly) {
            return;
        }
        if (!validateZeroValues()) {
            toaster('Quantity and price must be greater than 0', 'error', 'error');
            return;
        }
        // Validate required fields
        const validRows = rows.filter((row) => {
            const { quantity, price } = getRowValuesForSubmit(row);
            if (row.isMiscellaneous) {
                return row.subHeads && quantity && price;
            }
            return row.head_id && row.sub_head_id && quantity && price;
        });

        if (validRows.length === 0) {
            toaster('Please fill at least one complete row', 'error', 'error');
            return;
        }

        const event_id = getEventId();
        const travel_partner_id = getTravelPartnerId();

        if (!event_id) {
            toaster('Event ID is missing', 'error', 'error');
            return;
        }

        if (!travel_partner_id) {
            toaster('Travel partner ID is missing', 'error', 'error');
            return;
        }

        // Scroll to top and show confirmation modal
        if (!MobileWindow) {
            scrollToTop();
        }
        setShowConfirmSubmitModal(true);
    };

    const handleConfirmSubmit = async () => {
        if (!validateZeroValues()) {
            toaster('Quantity and price must be greater than 0', 'error', 'error');
            setShowConfirmSubmitModal(false);
            return;
        }
        // Validate required fields
        const validRows = rows.filter((row) => {
            const { quantity, price } = getRowValuesForSubmit(row);
            if (row.isMiscellaneous) {
                return row.subHeads && quantity && price;
            }
            return row.head_id && row.sub_head_id && quantity && price;
        });

        if (validRows.length === 0) {
            toaster('Please fill at least one complete row', 'error', 'error');
            setShowConfirmSubmitModal(false);
            return;
        }

        const event_id = getEventId();
        const travel_partner_id = getTravelPartnerId();

        if (!event_id) {
            toaster('Event ID is missing', 'error', 'error');
            setShowConfirmSubmitModal(false);
            return;
        }

        if (!travel_partner_id) {
            toaster('Travel partner ID is missing', 'error', 'error');
            setShowConfirmSubmitModal(false);
            return;
        }

        if (isAddInvoiceFlow) {
            if (!quotationsData?.quotation_id) {
                toaster('Quotation ID is missing', 'error', 'error');
                setShowConfirmSubmitModal(false);
                return;
            }

            const invoiceItems = validRows
                .filter((row) => row.item_id)
                .map((row) => {
                    const { size, quantity, price } = getRowValuesForSubmit(row);
                    return {
                        item_id: row.item_id,
                        invoice_size: size || "",
                        invoice_quantity: parseFloat(quantity) || 0,
                        invoice_price: parseFloat(price) || 0
                    };
                });

            if (invoiceItems.length === 0) {
                toaster('Please fill at least one valid item for invoice update', 'error', 'error');
                setShowConfirmSubmitModal(false);
                return;
            }

            const payload = {
                quotation_id: quotationsData.quotation_id,
                event_id,
                quotations: [
                    {
                        items: invoiceItems
                    }
                ]
            };

            setIsSubmittingQuotation(true);
            LoaderAnimation.show();
            try {
                const response = await editQuotationAPI(payload, 'invoice');
                if (response.error) {
                    toaster(response.message || 'Failed to update invoice', 'error', 'error');
                    setShowConfirmSubmitModal(false);
                    return;
                }
                toaster('Invoice updated successfully', 'success', 'success');
                setShowConfirmSubmitModal(false);
                setTimeout(() => {
                    history.goBack();
                }, 1000);
            } catch (error) {
                console.error('Error saving invoice:', error);
                const errorMessage = error?.response?.data?.message ||
                    error?.message ||
                    'Error saving invoice';
                toaster(errorMessage, 'error', 'error');
                setShowConfirmSubmitModal(false);
            } finally {
                setIsSubmittingQuotation(false);
                LoaderAnimation.hide();
            }
            return;
        }

        // Prepare quotations array
        const quotations = validRows.map((row) => {
            const { size, quantity, price } = getRowValuesForSubmit(row);
            if (row.isMiscellaneous) {
                // Use the head_id from the row (which should be from the dropdown option)
                const head_id = row.head_id?.toLowerCase() === "miscellaneous" ? "miscellaneous" : row.head_id || "miscellaneous";
                return {
                    head_id: head_id, // Use head_id from dropdown option mapping
                    sub_head_id: row.subHeads || null,
                    benchmark_price: parseFloat(price) || 0,
                    details: row.details || null,
                    description: row.description || null,
                    item_id: null,
                    qty: quantity || "1 no",
                    remarks: row.remarks || null,
                    size: size || null
                };
            }
            return {
                head_id: row.head_id,
                sub_head_id: row.sub_head_id,
                benchmark_price: parseFloat(price) || 0,
                details: row.details || null,
                item_id: row.item_id || null,
                qty: quantity || "1 no",
                remarks: row.remarks || null,
                size: size || null
            };
        });

        // Check if quotations exist to determine if we should create or update
        const hasExistingQuotations = quotationsData && quotationsData.quotations && quotationsData.quotations.length > 0;

        const payload = {
            event_id,
            travel_partner_id,
            quotations
        };

        // Add quotation_id if editing existing quotation
        if (hasExistingQuotations && quotationsData.quotation_id) {
            payload.quotation_id = quotationsData.quotation_id;
        }

        setIsSubmittingQuotation(true);
        LoaderAnimation.show();
        try {
            let response;
            if (hasExistingQuotations) {
                // Edit existing quotation
                response = await editQuotationAPI(payload);
                if (response.error) {
                    toaster(response.message || 'Failed to update quotation', 'error', 'error');
                    setShowConfirmSubmitModal(false);
                    return;
                }
                toaster('Quotation updated successfully', 'success', 'success');
            } else {
                // Create new quotation
                response = await uploadQuotationAPI(payload);
                if (response.error) {
                    toaster(response.message || 'Failed to upload quotation', 'error', 'error');
                    setShowConfirmSubmitModal(false);
                    return;
                }
                toaster('Quotation uploaded successfully', 'success', 'success');
            }
            setShowConfirmSubmitModal(false);
            // Navigate back after successful upload/update
            setTimeout(() => {
                history.goBack();
            }, 1000);
        } catch (error) {
            console.error('Error saving quotation:', error);
            // Extract error message from API response
            const errorMessage = error?.response?.data?.message ||
                error?.message ||
                'Error saving quotation';
            toaster(errorMessage, 'error', 'error');
            setShowConfirmSubmitModal(false);
        } finally {
            setIsSubmittingQuotation(false);
            LoaderAnimation.hide();
        }
    };

    // Handle Save as Draft button click
    const handleSaveAsDraftClick = () => {
        if (isQuotationReadOnly) {
            return;
        }
        // Scroll to top and show confirmation modal
        if (!MobileWindow) {
            scrollToTop();
        }
        setShowConfirmDraftModal(true);
    };

    // Handle Save as Draft confirmation
    const handleSaveAsDraft = async () => {
        if (!validateZeroValues()) {
            toaster('Quantity and price must be greater than 0', 'error', 'error');
            setShowConfirmDraftModal(false);
            return;
        }
        const event_id = getEventId();
        const travel_partner_id = getTravelPartnerId();

        if (!event_id) {
            toaster('Event ID is missing', 'error', 'error');
            setShowConfirmDraftModal(false);
            return;
        }

        if (!travel_partner_id) {
            toaster('Travel partner ID is missing', 'error', 'error');
            setShowConfirmDraftModal(false);
            return;
        }

        // Prepare quotations array from all rows (including incomplete ones for draft)
        const quotations = rows.map((row) => {
            if (row.isMiscellaneous) {
                const head_id = row.head_id?.toLowerCase() === "miscellaneous" ? "miscellaneous" : row.head_id || "miscellaneous";
                return {
                    head_id: head_id,
                    sub_head_id: row.subHeads || null,
                    benchmark_price: parseFloat(row.price) || 0,
                    details: row.details || null,
                    description: row.description || null,
                    item_id: null,
                    qty: row.quantity || "1 no",
                    remarks: row.remarks || null,
                    size: row.size || null
                };
            }
            return {
                head_id: row.head_id || null,
                sub_head_id: row.sub_head_id || null,
                benchmark_price: parseFloat(row.price) || 0,
                details: row.details || null,
                item_id: row.item_id || null,
                qty: row.quantity || "1 no",
                remarks: row.remarks || null,
                size: row.size || null
            };
        }).filter(row => row.head_id || row.description); // Filter out completely empty rows

        if (quotations.length === 0) {
            toaster('Please add at least one item to save as draft', 'error', 'error');
            setShowConfirmDraftModal(false);
            return;
        }

        // Check if quotations exist to determine if we should create or update
        const hasExistingQuotations = quotationsData && quotationsData.quotations && quotationsData.quotations.length > 0;

        const payload = {
            event_id,
            travel_partner_id,
            quotations
        };

        // Add quotation_id if editing existing quotation
        if (hasExistingQuotations && quotationsData.quotation_id) {
            payload.quotation_id = quotationsData.quotation_id;
        }

        setIsSavingDraft(true);
        LoaderAnimation.show();
        try {
            let response;
            if (hasExistingQuotations) {
                // Edit existing quotation
                response = await editQuotationAPI(payload, 'draft');
                if (response.error) {
                    toaster(response.message || 'Failed to save draft', 'error', 'error');
                    setShowConfirmDraftModal(false);
                    return;
                }
                toaster('Draft saved successfully', 'success', 'success');
            } else {
                // Create new quotation
                response = await uploadQuotationAPI(payload, 'draft');
                if (response.error) {
                    toaster(response.message || 'Failed to save draft', 'error', 'error');
                    setShowConfirmDraftModal(false);
                    return;
                }
                toaster('Draft saved successfully', 'success', 'success');
            }

            setShowConfirmDraftModal(false);

            // Refresh quotation data
            const refreshResponse = await listQuotationsAPI(event_id, travel_partner_id);
            if (!refreshResponse.error && refreshResponse.data?.data?.data && refreshResponse.data.data.data.length > 0) {
                const quotation = refreshResponse.data.data.data[0];
                setQuotationsData(quotation);
                setIsAdminEditApproved(quotation.is_admin_edit_approved !== false);

                // Update rows with refreshed quotation data using prefillQuotationData
                await prefillQuotationData(quotation);
            }
        } catch (error) {
            console.error('Error saving draft:', error);
            const errorMessage = error?.response?.data?.message ||
                error?.message ||
                'Error saving draft';
            toaster(errorMessage, 'error', 'error');
        } finally {
            setIsSavingDraft(false);
            LoaderAnimation.hide();
        }
    };

    // Handle Edit Quotation button click
    const handleEditQuotationClick = () => {
        if (isAdminEditApproved) {
            // Already approved - allow editing
            toaster('You can now edit the quotation', 'info', 'info');
            return;
        }

        // Check for pending edit requests
        if (quotationsData?.edit_requests && Array.isArray(quotationsData.edit_requests) && quotationsData.edit_requests.length > 0) {
            const hasPendingRequest = quotationsData.edit_requests.some(req => req.status === 'pending');
            if (hasPendingRequest) {
                // Scroll to top and show pending request modal
                if (!MobileWindow) {
                    scrollToTop();
                }
                setShowPendingRequestModal(true);
                return;
            }
        }
        // Scroll to top and show modal to request edit access
        if (!MobileWindow) {
            scrollToTop();
        }
        setShowEditRequestModal(true);
    };

    // Handle edit request submission
    const handleRequestEdit = async () => {
        const trimmedReason = editRequestReason?.trim() || '';
        if (!trimmedReason) {
            toaster('Please provide a reason for editing', 'error', 'error');
            return;
        }

        setIsRequestingEdit(true);
        LoaderAnimation.show();

        try {
            const event_id = getEventId();
            if (!event_id) {
                toaster('Event ID is missing', 'error', 'error');
                setIsRequestingEdit(false);
                LoaderAnimation.hide();
                return;
            }

            const payload = {
                event_id: event_id,
                reason: editRequestReason.trim()
            };

            const response = await requestEditQuotationAPI(payload);
            if (response.error) {
                toaster(response.message || 'Failed to submit edit request', 'error', 'error');
            } else {
                toaster(response?.data?.data?.message || 'Edit request submitted successfully', 'success', 'success');
                setShowEditRequestModal(false);
                setEditRequestReason("");

                // Refetch quotations to get updated edit_requests data
                const travel_partner_id = getTravelPartnerId();
                if (travel_partner_id) {
                    const refreshResponse = await listQuotationsAPI(event_id, travel_partner_id);
                    if (!refreshResponse.error && refreshResponse.data?.data?.data && refreshResponse.data.data.data.length > 0) {
                        const quotation = refreshResponse.data.data.data[0];
                        setQuotationsData(quotation);
                        setIsAdminEditApproved(quotation.is_admin_edit_approved !== false);
                    }
                }
            }
        } catch (error) {
            console.error('Error requesting edit:', error);
            toaster('Error submitting edit request', 'error', 'error');
        } finally {
            setIsRequestingEdit(false);
            LoaderAnimation.hide();
        }
    };

    // Modal components for edit request
    const getEditRequestModalHeader = () => {
        return <Text>Request Edit Access</Text>;
    };

    const getEditRequestModalBody = () => {
        return (
            <VStack space={4} p={4}>
                <VStack space={2} alignItems="center">
                    {noPhotosUploadedSVG}
                    <Text
                        fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                        fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                        color={COLORS.BLACK_COLOR}
                        textAlign="center"
                    >
                        Edit access is required. Please raise a request with a reason to edit the quotation.
                    </Text>
                </VStack>
                <Box>
                    <Text
                        fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                        fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                        color={COLORS.BLACK_COLOR}
                        mb={2}
                    >
                        Reason <Text color={COLORS.REJECT_TEXT_COLOR}>*</Text>
                    </Text>
                    <InputField
                        name="edit_request_reason"
                        placeholder="Enter reason for editing quotation"
                        value={editRequestReason || ''}
                        onChange={(e, name) => {
                            const newValue = e?.target?.value || '';
                            setEditRequestReason(newValue);
                        }}
                        forceRemount={showEditRequestModal}
                    />
                </Box>
            </VStack>
        );
    };

    const getEditRequestModalFooter = () => {
        return (
            <HStack space={3} justifyContent="end" width="100%">
                <CustomButton
                    text="Cancel"
                    onPress={() => {
                        setShowEditRequestModal(false);
                        setEditRequestReason("");
                    }}
                    isOutline
                    bgColor={COLORS.BLACK_COLOR}
                    textColor={COLORS.BLACK_COLOR}
                />
                <CustomButton
                    text={isRequestingEdit ? "Requesting..." : "Request Access"}
                    onPress={handleRequestEdit}
                    isDisabled={isRequestingEdit}
                    bgColor={COLORS.THEME_COLOR}
                    textColor={COLORS.WHITE_COLOR}
                />
            </HStack>
        );
    };

    // Modal components for confirmation submit
    const getConfirmSubmitModalHeader = () => {
        return <Text>{isAddInvoiceFlow ? "Confirm Update" : "Confirm Submission"}</Text>;
    };

    const getConfirmSubmitModalBody = () => {
        return (
            <VStack space={4} p={4}>
                <VStack space={2} alignItems="center">
                    {noPhotosUploadedSVG}
                    <Text
                        fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                        fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                        color={COLORS.BLACK_COLOR}
                        textAlign="center"
                    >
                        {isAddInvoiceFlow
                            ? "Are you sure you want to submit the invoice value? Please review all details carefully before submitting."
                            : "Are you sure you want to submit the quotation? Please review all details carefully before submitting."}
                    </Text>
                </VStack>
            </VStack>
        );
    };

    const getConfirmSubmitModalFooter = () => {
        return (
            <HStack space={3} justifyContent="end" width="100%">
                <CustomButton
                    text="Cancel"
                    onPress={() => {
                        setShowConfirmSubmitModal(false);
                    }}
                    isOutline
                    bgColor={COLORS.BLACK_COLOR}
                    textColor={COLORS.BLACK_COLOR}
                />
                <CustomButton
                    text={isSubmittingQuotation ? "Submitting..." : "Submit"}
                    onPress={handleConfirmSubmit}
                    isDisabled={isSubmittingQuotation}
                    bgColor={COLORS.THEME_COLOR}
                    textColor={COLORS.WHITE_COLOR}
                />
            </HStack>
        );
    };

    // Modal components for draft confirmation
    const getConfirmDraftModalHeader = () => {
        return <Text>Save as Draft</Text>;
    };

    const getConfirmDraftModalBody = () => {
        return (
            <VStack space={4} p={4}>
                <VStack space={2} alignItems="center">
                    {noPhotosUploadedSVG}
                    <Text
                        fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                        fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                        color={COLORS.BLACK_COLOR}
                        textAlign="center"
                    >
                        Are you sure you want to save this quotation as draft? Your progress will be saved and you can continue editing later.
                    </Text>
                </VStack>
            </VStack>
        );
    };

    const getConfirmDraftModalFooter = () => {
        return (
            <HStack space={3} justifyContent="end" width="100%">
                <CustomButton
                    text="Cancel"
                    onPress={() => {
                        setShowConfirmDraftModal(false);
                    }}
                    isOutline
                    bgColor={COLORS.BLACK_COLOR}
                    textColor={COLORS.BLACK_COLOR}
                />
                <CustomButton
                    text={isSavingDraft ? "Saving..." : "Save Draft"}
                    onPress={handleSaveAsDraft}
                    isDisabled={isSavingDraft}
                    bgColor={COLORS.THEME_COLOR}
                    textColor={COLORS.WHITE_COLOR}
                />
            </HStack>
        );
    };

    // Modal components for pending edit request
    const getPendingRequestModalHeader = () => {
        return <Text>Request for Edit Under Review</Text>;
    };

    const getPendingRequestModalBody = () => {
        return (
            <VStack space={4} p={4}>
                <VStack space={2} alignItems="center">
                    {noPhotosUploadedSVG}
                    <Text
                        fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                        fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                        color={COLORS.BLACK_COLOR}
                        textAlign="center"
                    >
                        Your edit request is currently under admin review. Once approved, you will be able to edit the quotation details.
                    </Text>
                </VStack>
            </VStack>
        );
    };

    const getPendingRequestModalFooter = () => {
        return (
            <HStack space={3} justifyContent="end" width="100%">
                <CustomButton
                    text="Okay"
                    onPress={() => {
                        setShowPendingRequestModal(false);
                    }}
                    bgColor={COLORS.THEME_COLOR}
                    textColor={COLORS.WHITE_COLOR}
                />
            </HStack>
        );
    };

    return (
        <Box
            bg={COLORS.THEME_BACKGROUND}
            pb={8}
            minHeight={'100vh'}
        >
            <Box
                bg={COLORS.WHITE_COLOR}
                py={1}
            >
                {MobileWindow ? (
                    <MobileHeader
                        showTabs={false}
                    />
                ) : (
                    <>
                        <WebHeader
                            showTabs={false} />
                        <Box pb={2} pt={4} px={"5rem"}>
                            <Breadcrumbs path={breadcrumbDetails} />
                        </Box>
                    </>
                )}
            </Box>

            <VStack
                ml={!MobileWindow && 8}
                mr={!MobileWindow && 8}
                px={MobileWindow ? '25px' : '50px'}
                py='30px'
                space={5}
                minH={'60vh'}
            >
                <HStack
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    flexWrap={MobileWindow ? 'wrap' : 'nowrap'}
                >
                    <CommonHeading text={'Quotation Sheet'} />
                    {quotationsData && !quotationsData.is_empty && canUserSubmitQuotation() && !isAddInvoiceFlow && (
                        !MobileWindow ? (
                            <CustomButton
                                pl={0}
                                text='Request Edit Access'
                                startIcon={editIconBannerImage}
                                width='fit-content'
                                height='46'
                                textColor={COLORS.WHITE_COLOR}
                                fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                                fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                                onPress={handleEditQuotationClick}
                                isDisabled={isAdminEditApproved || isQuotationReadOnly}
                            />
                        ) : (
                            <Pressable
                                onPress={handleEditQuotationClick}
                                disabled={isAdminEditApproved || isQuotationReadOnly}
                                opacity={isAdminEditApproved || isQuotationReadOnly ? 0.5 : 1}
                            >
                                <Avatar
                                    bg={'none'}
                                    size={'md'}
                                >
                                    {editIconBannerImage}
                                </Avatar>
                            </Pressable>
                        )
                    )}
                </HStack>

                <VStack space={4}>
                    {/* Logo and Vendor Details Section - Separate Boxes */}
                    <HStack space={4} alignItems="stretch" flexWrap={MobileWindow ? "wrap" : "nowrap"}>
                        {/* Logo Box - Left Side */}
                        <Box
                            p={5}
                            borderWidth={1}
                            borderColor={COLORS.BORDER_COLOR}
                            borderRadius="md"
                            bg={COLORS.WHITE_COLOR}
                            flex={MobileWindow ? "1 1 100%" : "0 0 auto"}
                            minWidth={MobileWindow ? "100%" : "auto"}
                            alignSelf="stretch"
                        >
                            {vendorDetails?.logo?.trim() ? (
                                <>
                                    <Text
                                        fontSize={COMMONFONTSIZE.LARGE_MEDIUM_FONT_SIZE}
                                        fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                                        mb={4}
                                    >
                                        Logo
                                    </Text>
                                    <ViewUploadImageCards
                                        ind={0}
                                        url={vendorDetails.logo}
                                        onDeleteImage={() => { }}
                                        permission={false}
                                        type="view"
                                    />
                                </>
                            ) : (
                                <Box
                                    justifyContent="center"
                                    alignItems="center"
                                    minHeight="150px"
                                >
                                    {noPhotosUploadedSVG}
                                    <Text
                                        fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                                        fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                                        color={COLORS.TEXT_COLOR || "#1a1a1a"}
                                        mt={2}
                                    >
                                        Logo not found!
                                    </Text>
                                </Box>
                            )}
                        </Box>
                        {/* Vendor Details Box - Right Side */}
                        <Box
                            p={5}
                            borderWidth={1}
                            borderColor={COLORS.BORDER_COLOR}
                            borderRadius="md"
                            bg={COLORS.WHITE_COLOR}
                            flex={MobileWindow ? "1 1 100%" : "1"}
                            minWidth={MobileWindow ? "100%" : "auto"}
                            alignSelf="stretch"
                        >
                            <Text
                                fontSize={COMMONFONTSIZE.LARGE_MEDIUM_FONT_SIZE}
                                fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                                mb={4}
                            >
                                Vendor Details
                            </Text>
                            {/* Table-like structure */}
                            <Box borderWidth={1} borderColor={COLORS.BORDER_COLOR} borderRadius="sm" overflow="hidden">
                                {/* Row 1: Event Name */}
                                <HStack borderBottomWidth={1} borderBottomColor={COLORS.BORDER_COLOR}>
                                    <Box
                                        p={3}
                                        borderRightWidth={1}
                                        borderRightColor={COLORS.BORDER_COLOR}
                                        bg="#f5f5f5"
                                        width="40%"
                                        minWidth="120px"
                                    >
                                        <Text fontSize="sm" fontWeight="medium" color={COLORS.TEXT_COLOR || "#1a1a1a"}>
                                            Event Name
                                        </Text>
                                    </Box>
                                    <Box p={3} flex={1}>
                                        <Text
                                            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                                            fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                                            color={COLORS.TEXT_COLOR || "#1a1a1a"}
                                            numberOfLines={1}
                                        >
                                            {getEventName()}
                                        </Text>
                                    </Box>
                                </HStack>
                                {/* Row 2: Name */}
                                <HStack borderBottomWidth={1} borderBottomColor={COLORS.BORDER_COLOR}>
                                    <Box
                                        p={3}
                                        borderRightWidth={1}
                                        borderRightColor={COLORS.BORDER_COLOR}
                                        bg="#f5f5f5"
                                        width="40%"
                                        minWidth="120px"
                                    >
                                        <Text fontSize="sm" fontWeight="medium" color={COLORS.TEXT_COLOR || "#1a1a1a"}>
                                            Name
                                        </Text>
                                    </Box>
                                    <Box p={3} flex={1}>
                                        <Text
                                            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                                            fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                                            color={COLORS.TEXT_COLOR || "#1a1a1a"}
                                            numberOfLines={1}
                                        >
                                            {vendorDetails.name}
                                        </Text>
                                    </Box>
                                </HStack>
                                {/* Row 3: Email */}
                                <HStack borderBottomWidth={1} borderBottomColor={COLORS.BORDER_COLOR}>
                                    <Box
                                        p={3}
                                        borderRightWidth={1}
                                        borderRightColor={COLORS.BORDER_COLOR}
                                        bg="#f5f5f5"
                                        width="40%"
                                        minWidth="120px"
                                    >
                                        <Text fontSize="sm" fontWeight="medium" color={COLORS.TEXT_COLOR || "#1a1a1a"}>
                                            Email
                                        </Text>
                                    </Box>
                                    <Box p={3} flex={1}>
                                        <Text
                                            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                                            fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                                            color={COLORS.TEXT_COLOR || "#1a1a1a"}
                                            numberOfLines={1}
                                        >
                                            {vendorDetails.email}
                                        </Text>
                                    </Box>
                                </HStack>
                                {/* Row 4: GST No. */}
                                <HStack borderBottomWidth={1} borderBottomColor={COLORS.BORDER_COLOR}>
                                    <Box
                                        p={3}
                                        borderRightWidth={1}
                                        borderRightColor={COLORS.BORDER_COLOR}
                                        bg="#f5f5f5"
                                        width="40%"
                                        minWidth="120px"
                                    >
                                        <Text fontSize="sm" fontWeight="medium" color={COLORS.TEXT_COLOR || "#1a1a1a"}>
                                            GST No.
                                        </Text>
                                    </Box>
                                    <Box p={3} flex={1}>
                                        <Text
                                            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                                            fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                                            color={COLORS.TEXT_COLOR || "#1a1a1a"}
                                            numberOfLines={1}
                                        >
                                            {vendorDetails.gstNo}
                                        </Text>
                                    </Box>
                                </HStack>
                                {/* Row 5: Company Name */}
                                <HStack>
                                    <Box
                                        p={3}
                                        borderRightWidth={1}
                                        borderRightColor={COLORS.BORDER_COLOR}
                                        bg="#f5f5f5"
                                        width="40%"
                                        minWidth="120px"
                                    >
                                        <Text fontSize="sm" fontWeight="medium" color={COLORS.TEXT_COLOR || "#1a1a1a"}>
                                            Company Name
                                        </Text>
                                    </Box>
                                    <Box p={3} flex={1}>
                                        <Text
                                            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                                            fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                                            color={COLORS.TEXT_COLOR || "#1a1a1a"}
                                            numberOfLines={1}
                                        >
                                            {vendorDetails.company}
                                        </Text>
                                    </Box>
                                </HStack>
                            </Box>
                        </Box>
                    </HStack>

                    {/* Table Section */}
                    <Box>
                        <HStack justifyContent="space-between" alignItems="center" mb={3} flexWrap={MobileWindow ? "wrap" : "nowrap"} space={3}>
                            <Text fontSize={COMMONFONTSIZE.LARGE_MEDIUM_FONT_SIZE}
                                fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                                {isAddInvoiceFlow ? "Quotation & Invoice Details" : "Quotation Details"}
                            </Text>
                            {!isAddInvoiceFlow && (
                                <HStack alignItems="center" space={2} flexWrap={MobileWindow ? "wrap" : "nowrap"}>
                                    <Box width={MobileWindow ? "100px" : "80px"}>
                                        <Dropdown
                                            name="add_row_count"
                                            selectedValues={addRowCount}
                                            setSelectedValues={setAddRowCount}
                                            dropItems={addRowCountOptions}
                                            placeholder="5"
                                            width="100%"
                                            isDisabled={isQuotationReadOnly}
                                        />
                                    </Box>
                                    <CustomButton
                                        text={`Add ${addRowCount} Row${parseInt(addRowCount) > 1 ? 's' : ''}`}
                                        onPress={handleAddRows}
                                        bgColor={COLORS.THEME_COLOR}
                                        textColor={COLORS.WHITE_COLOR}
                                        size="sm"
                                        isDisabled={isQuotationReadOnly}
                                    />
                                </HStack>
                            )}
                        </HStack>

                        {/* Table Container */}
                        <div className={isAddInvoiceFlow ? "invoice-flow" : ""}>
                          <Box
                              borderWidth={1}
                              borderColor={COLORS.BORDER_COLOR}
                              borderRadius="md"
                              overflow="hidden"
                              bg={COLORS.WHITE_COLOR}
                          >
                            {/* Table Header */}
                            {!isAddInvoiceFlow ? (
                                <HStack
                                    bg={COLORS.BORDER_COLOR}
                                    py={2}
                                    px={2}
                                    borderBottomWidth={1}
                                    borderBottomColor={COLORS.BORDER_COLOR}
                                    display={MobileWindow ? "none" : "flex"}
                                >
                                    <Box flex={2} px={2}>
                                        <Text fontSize="sm" fontWeight="bold">Head</Text>
                                    </Box>
                                    <Box flex={1.5} px={2}>
                                        <Text fontSize="sm" fontWeight="bold">Sub Head</Text>
                                    </Box>
                                    <Box flex={1.5} px={2}>
                                        <Text fontSize="sm" fontWeight="bold">Details</Text>
                                    </Box>
                                    <Box flex={1} px={2}>
                                        <Text fontSize="sm" fontWeight="bold">Size</Text>
                                    </Box>
                                    <Box flex={1} px={2}>
                                        <Text fontSize="sm" fontWeight="bold">Quantity</Text>
                                    </Box>
                                    <Box flex={1.5} px={2}>
                                        <Text fontSize="sm" fontWeight="bold">Price</Text>
                                    </Box>
                                    <Box flex={1.5} px={2}>
                                        <Text fontSize="sm" fontWeight="bold">Remarks</Text>
                                    </Box>
                                    {rows.length > 1 && !isAddInvoiceFlow && (
                                        <Box flex={0.5} px={2}>
                                            <Text fontSize="sm" fontWeight="bold">Action</Text>
                                        </Box>
                                    )}
                                </HStack>
                            ) : (
                                <VStack
                                    bg={COLORS.BORDER_COLOR}
                                    borderBottomWidth={1}
                                    borderBottomColor={COLORS.BORDER_COLOR}
                                    display={MobileWindow ? "none" : "flex"}
                                >
                                    <HStack
                                        py={0}
                                        px={2}
                                        borderBottomWidth={1}
                                        borderBottomColor={groupBorderColor}
                                        alignItems="stretch"
                                    >
                                        <Box flex={2} px={2} py={1} />
                                        <Box flex={1.5} px={2} py={1} />
                                        <Box flex={1.5} px={2} py={1} />
                                        <Box
                                            flex={3}
                                            px={2}
                                            py={2}
                                            alignItems="center"
                                            // borderLeftWidth={1}
                                            // borderLeftColor={groupBorderColor}
                                            // borderRightWidth={1}
                                            // borderRightColor={groupBorderColor}
                                        >
                                            <Text fontSize="sm" fontWeight="bold">Quotation</Text>
                                        </Box>
                                        <Box
                                            flex={3}
                                            px={2}
                                            py={2}
                                            alignItems="center"
                                            // borderLeftWidth={1}
                                            // borderLeftColor={groupBorderColor}
                                            // borderRightWidth={1}
                                            // borderRightColor={groupBorderColor}
                                        >
                                            <Text fontSize="sm" fontWeight="bold">Invoice</Text>
                                        </Box>
                                        <Box flex={1.5} px={2} py={1} />
                                    </HStack>
                                    <HStack
                                        py={0}
                                        px={2}
                                        borderTopWidth={0}
                                        alignItems="stretch"
                                    >
                                        <Box flex={2} px={2} py={2}>
                                            <Text fontSize="sm" fontWeight="bold">Head</Text>
                                        </Box>
                                        <Box flex={1.5} px={2} py={2}>
                                            <Text fontSize="sm" fontWeight="bold">Sub Head</Text>
                                        </Box>
                                        <Box flex={1.5} px={2} py={2}>
                                            <Text fontSize="sm" fontWeight="bold">Details</Text>
                                        </Box>
                                        <Box flex={1} px={2} py={2} borderLeftWidth={1} borderLeftColor={groupBorderColor}>
                                            <Text fontSize="sm" fontWeight="bold">Size</Text>
                                        </Box>
                                        <Box flex={1} px={2} py={2}>
                                            <Text fontSize="sm" fontWeight="bold">Quantity</Text>
                                        </Box>
                                        <Box flex={1} px={2} py={2} borderRightWidth={1} borderRightColor={groupBorderColor}>
                                            <Text fontSize="sm" fontWeight="bold">Price</Text>
                                        </Box>
                                        <Box flex={1} px={2} py={2}>
                                            <Text fontSize="sm" fontWeight="bold">Size</Text>
                                        </Box>
                                        <Box flex={1} px={2} py={2}>
                                            <Text fontSize="sm" fontWeight="bold">Quantity</Text>
                                        </Box>
                                        <Box flex={1} px={2} py={2} borderRightWidth={1} borderRightColor={groupBorderColor}>
                                            <Text fontSize="sm" fontWeight="bold">Price</Text>
                                        </Box>
                                        <Box flex={1.5} px={2} py={2}>
                                            <Text fontSize="sm" fontWeight="bold">Remarks</Text>
                                        </Box>
                                    </HStack>
                                </VStack>
                            )}

                            {/* Table Body */}
                            <VStack>
                                {rows.map((row, index) => (
                                    <Box
                                        key={row.id || `row-${index}`}
                                        className={row.isMiscellaneous ? "misc-flow" : ""}
                                        borderBottomWidth={index < rows.length - 1 ? 1 : 0}
                                        borderBottomColor={COLORS.BORDER_COLOR}
                                        bg={index % 2 === 0 ? COLORS.WHITE_COLOR : "gray.50"}
                                    >
                                        {MobileWindow ? (
                                            /* Mobile View: Stacked Layout */
                                            <VStack p={3} space={3}>
                                                <HStack justifyContent="space-between" alignItems="center">
                                                    <Text fontSize="sm" fontWeight="bold" flex={1}>
                                                        Row {index + 1}
                                                    </Text>
                                                    {rows.length > 1 && !isAddInvoiceFlow && (
                                                        <Pressable
                                                            onPress={() => removeRow(index)}
                                                            disabled={isQuotationReadOnly}
                                                            p={1}
                                                            borderRadius="md"
                                                            _pressed={{ bg: COLORS.BORDER_COLOR }}
                                                            opacity={isQuotationReadOnly ? 0.5 : 1}
                                                        >
                                                            <Svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                                {deleteCardsIconSVG}
                                                            </Svg>
                                                        </Pressable>
                                                    )}
                                                </HStack>
                                                <HStack space={2} alignItems="flex-end">
                                                    <Box flex={1}>
                                                        {row.showHead !== false ? (
                                                            <SearchableDropdown
                                                                label="Heads"
                                                                name={`heads_${index}`}
                                                                selectedValues={row.heads}
                                                                setSelectedValues={(value) => updateRow(index, "heads", value)}
                                                                dropItems={headsOptions}
                                                                placeholder="Head"
                                                                width="100%"
                                                                isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                            />
                                                        ) : (
                                                            <Box>
                                                                <Text fontSize="xs" color={COLORS.LABEL_TEXT_COLOR} mb={1}>Heads</Text>
                                                                <Box height="36px" />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    {row.showHead !== false && !isAddInvoiceFlow && (
                                                        <Pressable
                                                            onPress={() => addRowWithSameHead(index)}
                                                            p={2}
                                                            borderRadius="md"
                                                            bg={COLORS.THEME_COLOR}
                                                            _pressed={{ bg: COLORS.THEME_COLOR, opacity: 0.7 }}
                                                            disabled={isQuotationReadOnly || !row.heads || !row.head_id}
                                                            opacity={isQuotationReadOnly || !row.heads || !row.head_id ? 0.5 : 1}
                                                        >
                                                            <Svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                                {addIconSVG}
                                                            </Svg>
                                                        </Pressable>
                                                    )}
                                                </HStack>
                                                {row.isMiscellaneous ? (
                                                    <div className="misc-flow" style={{ display: "contents" }}>
                                                        <HStack space={2} alignItems="flex-end">
                                                            <Box flex={1}>
                                                                <InputField
                                                                    label="SubHeads"
                                                                    name={`subHeads_${index}`}
                                                                    value={row.subHeads || ""}
                                                                    onChange={(e) => updateRow(index, "subHeads", e.target.value)}
                                                                    placeholder="Enter subhead"
                                                                    width="100%"
                                                                    isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                                />
                                                            </Box>
                                                        </HStack>
                                                        <HStack space={2} alignItems="flex-end">
                                                            <Box flex={1}>
                                                                <InputField
                                                                    label="Details"
                                                                    name={`details_${index}`}
                                                                    value={row.details || ""}
                                                                    onChange={(e) => updateRow(index, "details", e.target.value)}
                                                                    placeholder="Enter details"
                                                                    width="100%"
                                                                    isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                                />
                                                            </Box>
                                                        </HStack>
                                                        <HStack space={2}>
                                                            {isAddInvoiceFlow ? (
                                                                <>
                                                                    <Box flex={1}>
                                                                        <InputField
                                                                            label="Quotation Size"
                                                                            name={`quotation_size_${index}`}
                                                                            value={row.size || ""}
                                                                            onChange={() => {}}
                                                                            placeholder="Quotation size"
                                                                            width="100%"
                                                                            isDisabled
                                                                        />
                                                                    </Box>
                                                                    <Box flex={1}>
                                                                        <InputField
                                                                            label="Quotation Quantity"
                                                                            name={`quotation_quantity_${index}`}
                                                                            value={row.quantity || ""}
                                                                            onChange={() => {}}
                                                                            placeholder="Quotation quantity"
                                                                            keyboardType="numeric"
                                                                            width="100%"
                                                                            isDisabled
                                                                        />
                                                                    </Box>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Box flex={1}>
                                                                        <InputField
                                                                            label="Size"
                                                                            name={`size_${index}`}
                                                                            value={row.size || ""}
                                                                            onChange={(e) => updateRow(index, "size", e.target.value)}
                                                                            placeholder="Enter size"
                                                                            width="100%"
                                                                            isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                                        />
                                                                    </Box>
                                                                    <Box flex={1}>
                                                                        <InputField
                                                                            label="Quantity"
                                                                            name={`quantity_${index}`}
                                                                            value={row.quantity || ""}
                                                                            onChange={(e) => updateRow(index, "quantity", e.target.value)}
                                                                            placeholder="Enter quantity"
                                                                            keyboardType="numeric"
                                                                            width="100%"
                                                                            isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                                            isInvalid={!!fieldErrors[row.id]?.quantity}
                                                                            errorMessage="Invalid"
                                                                        />
                                                                    </Box>
                                                                </>
                                                            )}
                                                        </HStack>
                                                        {isAddInvoiceFlow ? (
                                                            <>
                                                                <Box>
                                                                    <InputField
                                                                        label="Quotation Price"
                                                                        name={`quotation_price_${index}`}
                                                                        value={row.price || ""}
                                                                        onChange={() => {}}
                                                                        placeholder="Quotation price"
                                                                        keyboardType="numeric"
                                                                        width="100%"
                                                                        isDisabled
                                                                    />
                                                                </Box>
                                                                <HStack space={2}>
                                                                    <Box flex={1}>
                                                                        <InputField
                                                                            label="Invoice Size"
                                                                            name={`invoice_size_${index}`}
                                                                            value={row.invoiceSize || ""}
                                                                            onChange={(e) => updateRow(index, "invoiceSize", e.target.value)}
                                                                            placeholder="Invoice size"
                                                                            width="100%"
                                                                        />
                                                                    </Box>
                                                                    <Box flex={1}>
                                                                    <InputField
                                                                        label="Invoice Quantity"
                                                                        name={`invoice_quantity_${index}`}
                                                                        value={row.invoiceQuantity || ""}
                                                                        onChange={(e) => updateRow(index, "invoiceQuantity", e.target.value)}
                                                                        placeholder="Invoice quantity"
                                                                        keyboardType="numeric"
                                                                        width="100%"
                                                                        isInvalid={!!fieldErrors[row.id]?.invoiceQuantity}
                                                                        errorMessage="Invalid"
                                                                    />
                                                                    </Box>
                                                                </HStack>
                                                                <Box>
                                                                    <InputField
                                                                        label="Invoice Price"
                                                                        name={`invoice_price_${index}`}
                                                                        value={row.invoicePrice || ""}
                                                                        onChange={(e) => updateRow(index, "invoicePrice", e.target.value)}
                                                                        placeholder="Invoice price"
                                                                        keyboardType="numeric"
                                                                        width="100%"
                                                                        isInvalid={!!fieldErrors[row.id]?.invoicePrice}
                                                                        errorMessage="Invalid"
                                                                    />
                                                                </Box>
                                                            </>
                                                        ) : (
                                                            <Box>
                                                                <InputField
                                                                    label="Price"
                                                                    name={`price_${index}`}
                                                                    value={row.price || ""}
                                                                    onChange={(e) => updateRow(index, "price", e.target.value)}
                                                                    placeholder="Enter price"
                                                                    keyboardType="numeric"
                                                                    width="100%"
                                                                    isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                                    isInvalid={!!fieldErrors[row.id]?.price}
                                                                    errorMessage="Invalid"
                                                                />
                                                            </Box>
                                                        )}
                                                        <Box>
                                                            <InputField
                                                                label="Remarks"
                                                                name={`remarks_${index}`}
                                                                value={row.remarks || ""}
                                                                onChange={(e) => updateRow(index, "remarks", e.target.value)}
                                                                placeholder="Enter remarks"
                                                                width="100%"
                                                                isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                            />
                                                        </Box>
                                                        <Text fontSize="xs" color={COLORS.LABEL_TEXT_COLOR} fontStyle="italic">
                                                            Note: Excluded from Benchmarking Analysis
                                                        </Text>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <HStack space={2} alignItems="flex-end">
                                                            <Box flex={1}>
                                                                {row.showSubHead !== false ? (
                                                                    <SearchableDropdown
                                                                        label="SubHeads"
                                                                        name={`subHeads_${index}`}
                                                                        selectedValues={row.subHeads}
                                                                        setSelectedValues={(value) => updateRow(index, "subHeads", value)}
                                                                    dropItems={row.head_id ? (subHeadsOptionsMap[row.head_id] || []) : []}
                                                                    placeholder="Sub Head"
                                                                    width="100%"
                                                                    isDisabled={isAddInvoiceFlow || isQuotationReadOnly || !row.head_id || row.isMiscellaneous}
                                                                />
                                                                ) : (
                                                                    <Box>
                                                                        <Text fontSize="xs" color={COLORS.LABEL_TEXT_COLOR} mb={1}>SubHeads</Text>
                                                                        <Box height="36px" />
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                            {row.showSubHead !== false && !isAddInvoiceFlow && (
                                                                <Pressable
                                                                    onPress={() => addRowWithSameHeadSubHead(index)}
                                                                    p={2}
                                                                    borderRadius="md"
                                                                    bg={COLORS.THEME_COLOR}
                                                                    _pressed={{ bg: COLORS.THEME_COLOR, opacity: 0.7 }}
                                                                    disabled={isQuotationReadOnly || !row.heads || !row.head_id || !row.subHeads || !row.sub_head_id}
                                                                    opacity={isQuotationReadOnly || !row.heads || !row.head_id || !row.subHeads || !row.sub_head_id ? 0.5 : 1}
                                                                >
                                                                    <Svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                                        {addIconSVG}
                                                                    </Svg>
                                                                </Pressable>
                                                            )}
                                                        </HStack>
                                                        <HStack space={2} alignItems="flex-end">
                                                            <Box flex={1}>
                                                                {row.showDetails !== false ? (
                                                                    <SearchableDropdown
                                                                        label="Details"
                                                                        name={`details_${index}`}
                                                                        selectedValues={row.details}
                                                                        setSelectedValues={(value) => updateRow(index, "details", value)}
                                                                        dropItems={row.head_id && row.sub_head_id ? (detailsOptionsMap[`${row.head_id}_${row.sub_head_id}`] || []) : []}
                                                                        placeholder="Details"
                                                                        width="100%"
                                                                        isDisabled={isAddInvoiceFlow || isQuotationReadOnly || !row.head_id || !row.sub_head_id || row.isMiscellaneous}
                                                                    />
                                                                ) : (
                                                                    <Box>
                                                                        <Text fontSize="xs" color={COLORS.LABEL_TEXT_COLOR} mb={1}>Details</Text>
                                                                        <Box height="36px" />
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                            {row.showDetails !== false && !isAddInvoiceFlow && (
                                                                <Pressable
                                                                    onPress={() => addRowWithSameDetails(index)}
                                                                    p={2}
                                                                    borderRadius="md"
                                                                    bg={COLORS.THEME_COLOR}
                                                                    _pressed={{ bg: COLORS.THEME_COLOR, opacity: 0.7 }}
                                                                    disabled={isQuotationReadOnly || !row.heads || !row.head_id || !row.subHeads || !row.sub_head_id || !row.details}
                                                                    opacity={isQuotationReadOnly || !row.heads || !row.head_id || !row.subHeads || !row.sub_head_id || !row.details ? 0.5 : 1}
                                                                >
                                                                    <Svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                                        {addIconSVG}
                                                                    </Svg>
                                                                </Pressable>
                                                            )}
                                                        </HStack>
                                                        <HStack space={2}>
                                                            {isAddInvoiceFlow ? (
                                                                <>
                                                                    <Box flex={1}>
                                                                        <InputField
                                                                            label="Quotation Size"
                                                                            name={`quotation_size_${index}`}
                                                                            value={row.size || ""}
                                                                            onChange={() => {}}
                                                                            placeholder="Quotation size"
                                                                            width="100%"
                                                                            isDisabled
                                                                        />
                                                                    </Box>
                                                                    <Box flex={1}>
                                                                        <InputField
                                                                            label="Quotation Quantity"
                                                                            name={`quotation_quantity_${index}`}
                                                                            value={row.quantity || ""}
                                                                            onChange={() => {}}
                                                                            placeholder="Quotation quantity"
                                                                            keyboardType="numeric"
                                                                            width="100%"
                                                                            isDisabled
                                                                        />
                                                                    </Box>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Box flex={1}>
                                                                        <SearchableDropdown
                                                                            label="Size"
                                                                            name={`size_${index}`}
                                                                            selectedValues={row.size}
                                                                            setSelectedValues={(value) => updateRow(index, "size", value)}
                                                                            dropItems={getSizeOptions(row.head_id, row.sub_head_id, row.details)}
                                                                            placeholder="Size"
                                                                            width="100%"
                                                                            isDisabled={isAddInvoiceFlow || isQuotationReadOnly || !row.head_id || !row.sub_head_id || !row.details || row.isMiscellaneous}
                                                                        />
                                                                    </Box>
                                                                    <Box flex={1}>
                                                                        <InputField
                                                                            label="Quantity"
                                                                            name={`quantity_${index}`}
                                                                            value={row.quantity || ""}
                                                                            onChange={(e) => updateRow(index, "quantity", e.target.value)}
                                                                            placeholder="Enter quantity"
                                                                            keyboardType="numeric"
                                                                            width="100%"
                                                                            isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                                            isInvalid={!!fieldErrors[row.id]?.quantity}
                                                                            errorMessage="Invalid"
                                                                        />
                                                                    </Box>
                                                                </>
                                                            )}
                                                        </HStack>
                                                        {isAddInvoiceFlow ? (
                                                            <>
                                                                <Box>
                                                                    <InputField
                                                                        label="Quotation Price"
                                                                        name={`quotation_price_${index}`}
                                                                        value={row.price || ""}
                                                                        onChange={() => {}}
                                                                        placeholder="Quotation price"
                                                                        keyboardType="numeric"
                                                                        width="100%"
                                                                        isDisabled
                                                                    />
                                                                </Box>
                                                                <HStack space={2}>
                                                                    <Box flex={1}>
                                                                        <InputField
                                                                            label="Invoice Size"
                                                                            name={`invoice_size_${index}`}
                                                                            value={row.invoiceSize || ""}
                                                                            onChange={(e) => updateRow(index, "invoiceSize", e.target.value)}
                                                                            placeholder="Invoice size"
                                                                            width="100%"
                                                                        />
                                                                    </Box>
                                                                    <Box flex={1}>
                                                                    <InputField
                                                                        label="Invoice Quantity"
                                                                        name={`invoice_quantity_${index}`}
                                                                        value={row.invoiceQuantity || ""}
                                                                        onChange={(e) => updateRow(index, "invoiceQuantity", e.target.value)}
                                                                        placeholder="Invoice quantity"
                                                                        keyboardType="numeric"
                                                                        width="100%"
                                                                        isInvalid={!!fieldErrors[row.id]?.invoiceQuantity}
                                                                        errorMessage="Invalid"
                                                                    />
                                                                    </Box>
                                                                </HStack>
                                                                <Box>
                                                                    <InputField
                                                                        label="Invoice Price"
                                                                        name={`invoice_price_${index}`}
                                                                        value={row.invoicePrice || ""}
                                                                        onChange={(e) => updateRow(index, "invoicePrice", e.target.value)}
                                                                        placeholder="Invoice price"
                                                                        keyboardType="numeric"
                                                                        width="100%"
                                                                        isInvalid={!!fieldErrors[row.id]?.invoicePrice}
                                                                        errorMessage="Invalid"
                                                                    />
                                                                </Box>
                                                            </>
                                                        ) : (
                                                            <Box>
                                                                <InputField
                                                                    label="Price"
                                                                    name={`price_${index}`}
                                                                    value={row.price || ""}
                                                                    onChange={(e) => updateRow(index, "price", e.target.value)}
                                                                    placeholder="Enter price"
                                                                    keyboardType="numeric"
                                                                    width="100%"
                                                                    isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                                    isInvalid={!!fieldErrors[row.id]?.price}
                                                                    errorMessage="Invalid"
                                                                />
                                                            </Box>
                                                        )}
                                                        <Box>
                                                            <InputField
                                                                label="Remarks"
                                                                name={`remarks_${index}`}
                                                                value={row.remarks || ""}
                                                                onChange={(e) => updateRow(index, "remarks", e.target.value)}
                                                                placeholder="Enter remarks"
                                                                width="100%"
                                                                isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                            />
                                                        </Box>
                                                    </>
                                                )}
                                            </VStack>
                                        ) : (
                                            /* Desktop View: Table Row */
                                            <HStack py={isAddInvoiceFlow ? 0 : 2} px={2} alignItems={isAddInvoiceFlow ? "stretch" : "center"}>
                                                <Box flex={2} px={2} py={isAddInvoiceFlow ? 2 : 0}>
                                                    <HStack space={1} alignItems="center">
                                                        <Box flex={1}>
                                                            {row.showHead !== false ? (
                                                                <SearchableDropdown
                                                                    name={`heads_${index}`}
                                                                    selectedValues={row.heads}
                                                                    setSelectedValues={(value) => updateRow(index, "heads", value)}
                                                                    dropItems={headsOptions}
                                                                    placeholder="Head"
                                                                    width="100%"
                                                                    isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                                />
                                                            ) : (
                                                                <Box height="36px" />
                                                            )}
                                                        </Box>
                                                        {row.showHead !== false && !isAddInvoiceFlow && (
                                                            <Pressable
                                                                onPress={() => addRowWithSameHead(index)}
                                                                p={1.5}
                                                                borderRadius="md"
                                                                bg={COLORS.THEME_COLOR}
                                                                _pressed={{ bg: COLORS.THEME_COLOR, opacity: 0.7 }}
                                                                disabled={isQuotationReadOnly || !row.heads || !row.head_id}
                                                                opacity={isQuotationReadOnly || !row.heads || !row.head_id ? 0.5 : 1}
                                                            >
                                                                <Svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                                    {addIconSVG}
                                                                </Svg>
                                                            </Pressable>
                                                        )}
                                                    </HStack>
                                                </Box>
                                                {row.isMiscellaneous ? (
                                                    <div className="misc-flow" style={{ display: "contents" }}>
                                                        <Box flex={1.5} px={2} py={isAddInvoiceFlow ? 2 : 0}>
                                                            <InputField
                                                                name={`subHeads_${index}`}
                                                                value={row.subHeads || ""}
                                                                onChange={(e) => updateRow(index, "subHeads", e.target.value)}
                                                                placeholder="Enter subhead"
                                                                width="100%"
                                                                isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                            />
                                                        </Box>
                                                        <Box flex={1.5} px={2} py={isAddInvoiceFlow ? 2 : 0}>
                                                            <InputField
                                                                name={`details_${index}`}
                                                                value={row.details || ""}
                                                                onChange={(e) => updateRow(index, "details", e.target.value)}
                                                                placeholder="Enter details"
                                                                width="100%"
                                                                isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                            />
                                                        </Box>
                                                        <Box
                                                            flex={1}
                                                            px={2}
                                                            py={isAddInvoiceFlow ? 2 : 0}
                                                            borderLeftWidth={isAddInvoiceFlow ? 1 : 0}
                                                            borderLeftColor={groupBorderColor}
                                                            alignSelf={isAddInvoiceFlow ? "stretch" : "auto"}
                                                        >
                                                            {isAddInvoiceFlow ? (
                                                                <InputField
                                                                    name={`quotation_size_${index}`}
                                                                    value={row.size || ""}
                                                                    onChange={() => {}}
                                                                    placeholder="Quotation size"
                                                                    width="100%"
                                                                    isDisabled
                                                                />
                                                            ) : (
                                                                <InputField
                                                                    name={`size_${index}`}
                                                                    value={row.size || ""}
                                                                    onChange={(e) => updateRow(index, "size", e.target.value)}
                                                                    placeholder="Enter size"
                                                                    width="100%"
                                                                    isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                                />
                                                            )}
                                                        </Box>
                                                        <Box flex={1} px={2} py={isAddInvoiceFlow ? 2 : 0}>
                                                            {isAddInvoiceFlow ? (
                                                                <InputField
                                                                    name={`quotation_quantity_${index}`}
                                                                    value={row.quantity || ""}
                                                                    onChange={() => {}}
                                                                    placeholder="Quotation quantity"
                                                                    keyboardType="numeric"
                                                                    width="100%"
                                                                    isDisabled
                                                                />
                                                            ) : (
                                                                <InputField
                                                                    name={`quantity_${index}`}
                                                                    value={row.quantity || ""}
                                                                    onChange={(e) => updateRow(index, "quantity", e.target.value)}
                                                                    placeholder="Enter quantity"
                                                                    keyboardType="numeric"
                                                                    width="100%"
                                                                    isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                                    isInvalid={!!fieldErrors[row.id]?.quantity}
                                                                    errorMessage="Invalid"
                                                                />
                                                            )}
                                                        </Box>
                                                        <Box
                                                            flex={isAddInvoiceFlow ? 1 : 1.5}
                                                            px={2}
                                                            py={isAddInvoiceFlow ? 2 : 0}
                                                            borderRightWidth={0}
                                                            borderRightColor={groupBorderColor}
                                                            alignSelf={isAddInvoiceFlow ? "stretch" : "auto"}
                                                        >
                                                            {isAddInvoiceFlow ? (
                                                                <InputField
                                                                    key={`quotation_price_misc_${index}_${row.head_id || ''}`}
                                                                    name={`quotation_price_${index}`}
                                                                    value={row.price || ""}
                                                                    onChange={() => {}}
                                                                    placeholder="Quotation price"
                                                                    keyboardType="numeric"
                                                                    width="100%"
                                                                    isDisabled
                                                                />
                                                            ) : (
                                                                <InputField
                                                                    key={`price_misc_${index}_${row.head_id || ''}`}
                                                                    name={`price_${index}`}
                                                                    value={row.price || ""}
                                                                    onChange={(e) => updateRow(index, "price", e.target.value)}
                                                                    placeholder="Enter price"
                                                                    keyboardType="numeric"
                                                                    width="100%"
                                                                    isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                                    isInvalid={!!fieldErrors[row.id]?.price}
                                                                    errorMessage="Invalid"
                                                                />
                                                            )}
                                                        </Box>
                                                        {isAddInvoiceFlow && (
                                                            <>
                                                                <Box
                                                                    flex={1}
                                                                    px={2}
                                                                    py={isAddInvoiceFlow ? 2 : 0}
                                                                    borderLeftWidth={1}
                                                                    borderLeftColor={groupBorderColor}
                                                                    alignSelf={isAddInvoiceFlow ? "stretch" : "auto"}
                                                                >
                                                                    <InputField
                                                                        name={`invoice_size_${index}`}
                                                                        value={row.invoiceSize || ""}
                                                                        onChange={(e) => updateRow(index, "invoiceSize", e.target.value)}
                                                                        placeholder="Invoice size"
                                                                        width="100%"
                                                                    />
                                                                </Box>
                                                                <Box flex={1} px={2} py={isAddInvoiceFlow ? 2 : 0}>
                                                                    <InputField
                                                                        name={`invoice_quantity_${index}`}
                                                                        value={row.invoiceQuantity || ""}
                                                                        onChange={(e) => updateRow(index, "invoiceQuantity", e.target.value)}
                                                                        placeholder="Invoice quantity"
                                                                        keyboardType="numeric"
                                                                        width="100%"
                                                                        isInvalid={!!fieldErrors[row.id]?.invoiceQuantity}
                                                                        errorMessage="Invalid"
                                                                    />
                                                                </Box>
                                                                <Box
                                                                    flex={1}
                                                                    px={2}
                                                                    py={isAddInvoiceFlow ? 2 : 0}
                                                                    borderRightWidth={1}
                                                                    borderRightColor={groupBorderColor}
                                                                    alignSelf={isAddInvoiceFlow ? "stretch" : "auto"}
                                                                >
                                                                    <InputField
                                                                        name={`invoice_price_${index}`}
                                                                        value={row.invoicePrice || ""}
                                                                        onChange={(e) => updateRow(index, "invoicePrice", e.target.value)}
                                                                        placeholder="Invoice price"
                                                                        keyboardType="numeric"
                                                                        width="100%"
                                                                        isInvalid={!!fieldErrors[row.id]?.invoicePrice}
                                                                        errorMessage="Invalid"
                                                                    />
                                                                </Box>
                                                            </>
                                                        )}
                                                        <Box flex={1.5} px={2} py={isAddInvoiceFlow ? 2 : 0}>
                                                            <InputField
                                                                key={`remarks_misc_${index}_${row.head_id || ''}`}
                                                                name={`remarks_${index}`}
                                                                value={row.remarks || ""}
                                                                onChange={(e) => updateRow(index, "remarks", e.target.value)}
                                                                placeholder="Enter remarks"
                                                                width="100%"
                                                                isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                            />
                                                        </Box>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <Box flex={1.5} px={2} py={isAddInvoiceFlow ? 2 : 0}>
                                                            <HStack space={1} alignItems="center">
                                                                <Box flex={1}>
                                                                    {row.showSubHead !== false ? (
                                                                        <SearchableDropdown
                                                                            key={`subHeads_${index}_${row.head_id || ''}`}
                                                                            name={`subHeads_${index}`}
                                                                            selectedValues={row.subHeads || undefined}
                                                                            setSelectedValues={(value) => updateRow(index, "subHeads", value)}
                                                                            dropItems={row.head_id ? (subHeadsOptionsMap[row.head_id] || []) : []}
                                                                            placeholder="Sub Head"
                                                                            width="100%"
                                                                            isDisabled={isAddInvoiceFlow || isQuotationReadOnly || !row.head_id || row.isMiscellaneous}
                                                                        />
                                                                    ) : (
                                                                        <Box height="36px" />
                                                                    )}
                                                                </Box>
                                                                {row.showSubHead !== false && !isAddInvoiceFlow && (
                                                                    <Pressable
                                                                        onPress={() => addRowWithSameHeadSubHead(index)}
                                                                        p={1.5}
                                                                        borderRadius="md"
                                                                        bg={COLORS.THEME_COLOR}
                                                                        _pressed={{ bg: COLORS.THEME_COLOR, opacity: 0.7 }}
                                                                        disabled={isQuotationReadOnly || !row.heads || !row.head_id || !row.subHeads || !row.sub_head_id}
                                                                        opacity={isQuotationReadOnly || !row.heads || !row.head_id || !row.subHeads || !row.sub_head_id ? 0.5 : 1}
                                                                    >
                                                                        <Svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                                            {addIconSVG}
                                                                        </Svg>
                                                                    </Pressable>
                                                                )}
                                                            </HStack>
                                                        </Box>
                                                        <Box flex={1.5} px={2} py={isAddInvoiceFlow ? 2 : 0}>
                                                            <HStack space={1} alignItems="center">
                                                                <Box flex={1}>
                                                                    {row.showDetails !== false ? (
                                                                        <SearchableDropdown
                                                                            key={`details_${index}_${row.head_id || ''}_${row.sub_head_id || ''}`}
                                                                            name={`details_${index}`}
                                                                            selectedValues={row.details || undefined}
                                                                            setSelectedValues={(value) => updateRow(index, "details", value)}
                                                                            dropItems={row.head_id && row.sub_head_id ? (detailsOptionsMap[`${row.head_id}_${row.sub_head_id}`] || []) : []}
                                                                            placeholder="Details"
                                                                            width="100%"
                                                                            isDisabled={isAddInvoiceFlow || isQuotationReadOnly || !row.head_id || !row.sub_head_id || row.isMiscellaneous}
                                                                        />
                                                                    ) : (
                                                                        <Box height="36px" />
                                                                    )}
                                                                </Box>
                                                                {row.showDetails !== false && !isAddInvoiceFlow && (
                                                                <Pressable
                                                                    onPress={() => addRowWithSameDetails(index)}
                                                                    p={1.5}
                                                                    borderRadius="md"
                                                                    bg={COLORS.THEME_COLOR}
                                                                    _pressed={{ bg: COLORS.THEME_COLOR, opacity: 0.7 }}
                                                                    disabled={isQuotationReadOnly || !row.heads || !row.head_id || !row.subHeads || !row.sub_head_id || !row.details}
                                                                    opacity={isQuotationReadOnly || !row.heads || !row.head_id || !row.subHeads || !row.sub_head_id || !row.details ? 0.5 : 1}
                                                                >
                                                                    <Svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                                        {addIconSVG}
                                                                    </Svg>
                                                                </Pressable>
                                                                )}
                                                            </HStack>
                                                        </Box>
                                                        {isAddInvoiceFlow ? (
                                                            <>
                                                                <Box
                                                                    flex={1}
                                                                    px={2}
                                                                    py={isAddInvoiceFlow ? 2 : 0}
                                                                    borderLeftWidth={1}
                                                                    borderLeftColor={groupBorderColor}
                                                                    alignSelf={isAddInvoiceFlow ? "stretch" : "auto"}
                                                                >
                                                                    <InputField
                                                                        key={`quotation_size_${index}_${row.head_id || ''}_${row.sub_head_id || ''}_${row.details || ''}`}
                                                                        name={`quotation_size_${index}`}
                                                                        value={row.size || ""}
                                                                        onChange={() => {}}
                                                                        placeholder="Quotation size"
                                                                        width="100%"
                                                                        isDisabled
                                                                    />
                                                                </Box>
                                                                <Box flex={1} px={2} py={isAddInvoiceFlow ? 2 : 0}>
                                                                    <InputField
                                                                        key={`quotation_quantity_${index}_${row.head_id || ''}_${row.sub_head_id || ''}_${row.details || ''}`}
                                                                        name={`quotation_quantity_${index}`}
                                                                        value={row.quantity || ""}
                                                                        onChange={() => {}}
                                                                        placeholder="Quotation quantity"
                                                                        keyboardType="numeric"
                                                                        width="100%"
                                                                        isDisabled
                                                                    />
                                                                </Box>
                                                                <Box
                                                                    flex={1}
                                                                    px={2}
                                                                    py={isAddInvoiceFlow ? 2 : 0}
                                                                    borderRightWidth={1}
                                                                    borderRightColor={groupBorderColor}
                                                                    alignSelf={isAddInvoiceFlow ? "stretch" : "auto"}
                                                                >
                                                                    <InputField
                                                                        key={`quotation_price_${index}_${row.head_id || ''}_${row.sub_head_id || ''}_${row.details || ''}`}
                                                                        name={`quotation_price_${index}`}
                                                                        value={row.price || ""}
                                                                        onChange={() => {}}
                                                                        placeholder="Quotation price"
                                                                        keyboardType="numeric"
                                                                        width="100%"
                                                                        isDisabled
                                                                    />
                                                                </Box>
                                                                <Box flex={1} px={2} py={isAddInvoiceFlow ? 2 : 0} borderLeftWidth={0} borderLeftColor={groupBorderColor}>
                                                                    <InputField
                                                                        key={`invoice_size_${index}_${row.head_id || ''}_${row.sub_head_id || ''}_${row.details || ''}`}
                                                                        name={`invoice_size_${index}`}
                                                                        value={row.invoiceSize || ""}
                                                                        onChange={(e) => updateRow(index, "invoiceSize", e.target.value)}
                                                                        placeholder="Invoice size"
                                                                        width="100%"
                                                                    />
                                                                </Box>
                                                                <Box flex={1} px={2} py={isAddInvoiceFlow ? 2 : 0}>
                                                                    <InputField
                                                                        key={`invoice_quantity_${index}_${row.head_id || ''}_${row.sub_head_id || ''}_${row.details || ''}`}
                                                                        name={`invoice_quantity_${index}`}
                                                                        value={row.invoiceQuantity || ""}
                                                                        onChange={(e) => updateRow(index, "invoiceQuantity", e.target.value)}
                                                                        placeholder="Invoice quantity"
                                                                        keyboardType="numeric"
                                                                        width="100%"
                                                                        isInvalid={!!fieldErrors[row.id]?.invoiceQuantity}
                                                                        errorMessage="Invalid"
                                                                    />
                                                                </Box>
                                                                <Box
                                                                    flex={1}
                                                                    px={2}
                                                                    py={isAddInvoiceFlow ? 2 : 0}
                                                                    borderRightWidth={1}
                                                                    borderRightColor={groupBorderColor}
                                                                    alignSelf={isAddInvoiceFlow ? "stretch" : "auto"}
                                                                >
                                                                    <InputField
                                                                        key={`invoice_price_${index}_${row.head_id || ''}_${row.sub_head_id || ''}_${row.details || ''}`}
                                                                        name={`invoice_price_${index}`}
                                                                        value={row.invoicePrice || ""}
                                                                        onChange={(e) => updateRow(index, "invoicePrice", e.target.value)}
                                                                        placeholder="Invoice price"
                                                                        keyboardType="numeric"
                                                                        width="100%"
                                                                        isInvalid={!!fieldErrors[row.id]?.invoicePrice}
                                                                        errorMessage="Invalid"
                                                                    />
                                                                </Box>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Box flex={1} px={2} py={isAddInvoiceFlow ? 2 : 0}>
                                                                    <SearchableDropdown
                                                                        key={`size_${index}_${row.head_id || ''}_${row.sub_head_id || ''}_${row.details || ''}`}
                                                                        name={`size_${index}`}
                                                                        selectedValues={row.size || undefined}
                                                                        setSelectedValues={(value) => updateRow(index, "size", value)}
                                                                        dropItems={getSizeOptions(row.head_id, row.sub_head_id, row.details)}
                                                                        placeholder="Size"
                                                                        width="100%"
                                                                        isDisabled={isAddInvoiceFlow || isQuotationReadOnly || !row.head_id || !row.sub_head_id || !row.details || row.isMiscellaneous}
                                                                    />
                                                                </Box>
                                                                <Box flex={1} px={2} py={isAddInvoiceFlow ? 2 : 0}>
                                                                    <InputField
                                                                        key={`quantity_${index}_${row.head_id || ''}_${row.sub_head_id || ''}_${row.details || ''}`}
                                                                        name={`quantity_${index}`}
                                                                        value={row.quantity || ""}
                                                                        onChange={(e) => updateRow(index, "quantity", e.target.value)}
                                                                        placeholder="Quantity"
                                                                        keyboardType="numeric"
                                                                        width="100%"
                                                                        isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                                        isInvalid={!!fieldErrors[row.id]?.quantity}
                                                                        errorMessage="Invalid"
                                                                    />
                                                                </Box>
                                                                <Box flex={1.5} px={2} py={isAddInvoiceFlow ? 2 : 0}>
                                                                    <InputField
                                                                        key={`price_${index}_${row.head_id || ''}_${row.sub_head_id || ''}_${row.details || ''}`}
                                                                        name={`price_${index}`}
                                                                        value={row.price || ""}
                                                                        onChange={(e) => updateRow(index, "price", e.target.value)}
                                                                        placeholder="Price"
                                                                        keyboardType="numeric"
                                                                        width="100%"
                                                                        isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                                        isInvalid={!!fieldErrors[row.id]?.price}
                                                                        errorMessage="Invalid"
                                                                    />
                                                                </Box>
                                                            </>
                                                        )}
                                                        <Box flex={1.5} px={2} py={isAddInvoiceFlow ? 2 : 0}>
                                                            <InputField
                                                                key={`remarks_${index}_${row.head_id || ''}_${row.sub_head_id || ''}_${row.details || ''}`}
                                                                name={`remarks_${index}`}
                                                                value={row.remarks || ""}
                                                                onChange={(e) => updateRow(index, "remarks", e.target.value)}
                                                                placeholder="Remarks"
                                                                width="100%"
                                                                isDisabled={isAddInvoiceFlow || isQuotationReadOnly}
                                                            />
                                                        </Box>
                                                    </>
                                                )}
                                                {rows.length > 1 && !isAddInvoiceFlow && (
                                                    <Box flex={0.5} px={2} py={isAddInvoiceFlow ? 2 : 0} alignItems="center">
                                                        <Pressable
                                                            onPress={() => removeRow(index)}
                                                            disabled={isQuotationReadOnly}
                                                            p={1}
                                                            borderRadius="md"
                                                            _pressed={{ bg: COLORS.BORDER_COLOR }}
                                                            opacity={isQuotationReadOnly ? 0.5 : 1}
                                                        >
                                                            <Svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                                                {deleteCardsIconSVG}
                                                            </Svg>
                                                        </Pressable>
                                                    </Box>
                                                )}
                                            </HStack>
                                        )}
                                    </Box>
                                ))}
                            </VStack>
                          </Box>
                        </div>

                        {/* Management Fee and Taxes Section - Commented out */}
                        {/* <VStack space={3} mt={4}>
                            <HStack space={3} alignItems="center">
                                <Box flex={1}>
                                    <Text fontSize="sm" fontWeight="bold" mb={2}>
                                        Management Fee
                                    </Text>
                                    <InputField
                                        name="management_fee"
                                        value=""
                                        onChange={() => { }}
                                        placeholder="Enter Management Fee"
                                        keyboardType="numeric"
                                        width="100%"
                                    />
                                </Box>
                                <Box flex={1}>
                                    <Text fontSize="sm" fontWeight="bold" mb={2}>
                                        Taxes
                                    </Text>
                                    <InputField
                                        name="taxes"
                                        value=""
                                        onChange={() => { }}
                                        placeholder="Enter Taxes"
                                        keyboardType="numeric"
                                        width="100%"
                                    />
                                </Box>
                            </HStack>
                        </VStack> */}
                    </Box>

                    {/* Save as Draft and Upload/Update Buttons */}
                    <HStack justifyContent="flex-end" width="100%" mt={4} space={3}>
                        {isAddInvoiceFlow ? (
                            <CustomButton
                                onPress={handleUpload}
                                text="Update Invoice Value"
                                color={COLORS.WHITE_COLOR}
                                isDisabled={isQuotationReadOnly}
                            />
                        ) : (
                            <>
                                <CustomButton
                                    onPress={handleSaveAsDraftClick}
                                    text="Save as Draft"
                                    isOutline
                                    borderColor={COLORS.THEME_COLOR}
                                    textColor={COLORS.THEME_COLOR}
                                    isDisabled={isSavingDraft || isQuotationReadOnly}
                                />
                                {canUserSubmitQuotation() && (
                                    <CustomButton
                                        onPress={handleUpload}
                                        text={quotationsData && quotationsData.quotations && quotationsData.quotations.length > 0 ? "Confirm & Update" : "Upload"}
                                        color={COLORS.WHITE_COLOR}
                                        isDisabled={isQuotationReadOnly}
                                    />
                                )}
                            </>
                        )}
                    </HStack>
                </VStack>
            </VStack>

            {/* Edit Request Modal */}
            {!MobileWindow ? (
                <CustomModal
                    top='30px'
                    isOpen={showEditRequestModal}
                    onClose={() => {
                        setShowEditRequestModal(false);
                        setEditRequestReason("");
                    }}
                    header={getEditRequestModalHeader()}
                    body={getEditRequestModalBody()}
                    footer={getEditRequestModalFooter()}
                    width='40%'
                    height='auto'
                />
            ) : (
                <MobileModelSheet
                    isOpen={showEditRequestModal}
                    onClose={() => {
                        setShowEditRequestModal(false);
                        setEditRequestReason("");
                    }}
                    header={getEditRequestModalHeader()}
                    body={getEditRequestModalBody()}
                    footer={getEditRequestModalFooter()}
                />
            )}

            {/* Confirm Submit Modal */}
            {!MobileWindow ? (
                <CustomModal
                    top='30px'
                    isOpen={showConfirmSubmitModal}
                    onClose={() => {
                        setShowConfirmSubmitModal(false);
                    }}
                    header={getConfirmSubmitModalHeader()}
                    body={getConfirmSubmitModalBody()}
                    footer={getConfirmSubmitModalFooter()}
                    width='40%'
                    height='auto'
                />
            ) : (
                <MobileModelSheet
                    isOpen={showConfirmSubmitModal}
                    onClose={() => {
                        setShowConfirmSubmitModal(false);
                    }}
                    header={getConfirmSubmitModalHeader()}
                    body={getConfirmSubmitModalBody()}
                    footer={getConfirmSubmitModalFooter()}
                />
            )}

            {/* Confirm Draft Modal */}
            {!MobileWindow ? (
                <CustomModal
                    top='30px'
                    isOpen={showConfirmDraftModal}
                    onClose={() => {
                        setShowConfirmDraftModal(false);
                    }}
                    header={getConfirmDraftModalHeader()}
                    body={getConfirmDraftModalBody()}
                    footer={getConfirmDraftModalFooter()}
                    width='40%'
                    height='auto'
                />
            ) : (
                <MobileModelSheet
                    isOpen={showConfirmDraftModal}
                    onClose={() => {
                        setShowConfirmDraftModal(false);
                    }}
                    header={getConfirmDraftModalHeader()}
                    body={getConfirmDraftModalBody()}
                    footer={getConfirmDraftModalFooter()}
                />
            )}

            {/* Pending Request Modal */}
            {!MobileWindow ? (
                <CustomModal
                    top='30px'
                    isOpen={showPendingRequestModal}
                    onClose={() => {
                        setShowPendingRequestModal(false);
                    }}
                    header={getPendingRequestModalHeader()}
                    body={getPendingRequestModalBody()}
                    footer={getPendingRequestModalFooter()}
                    width='40%'
                    height='auto'
                />
            ) : (
                <MobileModelSheet
                    isOpen={showPendingRequestModal}
                    onClose={() => {
                        setShowPendingRequestModal(false);
                    }}
                    header={getPendingRequestModalHeader()}
                    body={getPendingRequestModalBody()}
                    footer={getPendingRequestModalFooter()}
                />
            )}
        </Box>
    );
};

export default QuotationsPage;
